// src/components/desk/CooperationChips.jsx — THE one renderer for "cooperations of a
// property" (chips + modal-grade list) and its label/pick helpers, extracted VERBATIM
// from IntakeTab.jsx (DESK_MODE_REWRITE_PLAN §3 decomposition, behavior-preserving).
// CooperationModal deliberately stays in IntakeTab — this file only takes callbacks.
// Status/intent key maps, VIEWING_STATUS_STYLE and the coopMeetingSeed/coopIntentOf
// seed helpers are exported: IntakeTab (modal, lineage modal, meeting seeds) and
// HistoryCards consume them.

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { t, tLog } from '../../lib/i18n.js'
import { Icon } from '../Icon.jsx'
import { MEETING_STATUS_KEYS, MEETING_ACTIVE } from './MeetingsFeed.jsx'
import { ContactPill } from './ContactPill.jsx'
import { ShareMenu } from './ShareMenu.jsx'
// The agent deep link now lives on THIS pill (owner 2026-09-08) — see LinksMenu below.
import { listingDeepLink } from '../../lib/deskUrl.js'
import { PillOverflow, pills } from './PillOverflow.jsx'
import { popoverPos, useCloseOnPageMove, useHoverCapable, pill, timeAgo, ROW, ROW_LEFT, ROW_VAL } from './primitives.jsx'
import { bestCooperation } from '../../lib/coopMath.js'
import { moveInPayment, moveInLabel } from '../../lib/tenancyTerms.js'
import { fmtTbilisi } from '../../lib/tbilisiTime.js'
import { MeetingCountdown } from './MeetingCountdown.jsx'
import AiMark, { aiMarkFor } from './AiMark.jsx'
import { getRestrictionVocab, getAxisExpansion, listRestrictionRules } from '../../db/core.js'
import { ruleSentence, isExceptionOf } from '../../lib/restrictionSentence.js'

let restrictionFaceExpansion = null
let restrictionFaceExpansionPromise = null
// ⚠️ Without this, subsumption still works for "no pets except a cat" but MISSES every descent
// through the expansion table — accept_only{foreigner} + exclude{Indian} was 3 of the 38 live
// pairs. Measuring without it reported a floor and looked like an answer.
const loadRestrictionFaceExpansion = () => restrictionFaceExpansion
  ? Promise.resolve(restrictionFaceExpansion)
  : (restrictionFaceExpansionPromise ||= getAxisExpansion()
      .then(x => (restrictionFaceExpansion = x || {}))
      .catch(() => ({})))
let restrictionFaceVocab = null
let restrictionFaceVocabPromise = null
const loadRestrictionFaceVocab = () => restrictionFaceVocab
  ? Promise.resolve(restrictionFaceVocab)
  : (restrictionFaceVocabPromise ||= getRestrictionVocab().then(v => (restrictionFaceVocab = v)))

function useStructuredRestrictionFace(listingId) {
  const [items, setItems] = useState([])
  useEffect(() => {
    let gone = false
    const load = () => {
      if (!listingId) { setItems([]); return }
      Promise.all([loadRestrictionFaceVocab(), loadRestrictionFaceExpansion(), listRestrictionRules(listingId)])
        .then(([vocab, expansion, response]) => {
          if (gone) return
          const tone = { exclude: 'not_allowed', accept_only: 'only', allow: 'allowed', prefer: 'preferred' }
          const rules = (response?.rules || []).filter(r => r.origin !== 'legacy_stance')

          // ⚠️ AN EXCEPTION IS NOT A CONTRADICTION (owner-reported: "no students except girls"
          // printed as ✗ students / ❗ girls). This band listed every rule as a peer, so a
          // parent and the carve that narrows it argued with each other on the card face —
          // "✗ cat, dog, other" beside "✓ small dog" reads as "no pets" and "pets fine" at
          // once. The panel has always known better; it nests the pair. The knowledge simply
          // was not shared, so it is now (isExceptionOf, lib/restrictionSentence.js).
          //
          // ⚠️ THE CARVE IS KEPT, NOT DROPPED. Suppressing it would be the easy fix and wrong
          // in the other direction: an agent scanning cards would read "no pets" and skip a
          // flat that takes a small dog. So the exception stays and is MARKED as one — it
          // borrows its parent's tone (a carve is part of the same restriction, not an
          // opposing one) and says "გარდა". Same vocabulary the panel's own nested row uses.
          const exceptionOf = new Map()
          for (const parent of rules) for (const other of rules)
            if (!exceptionOf.has(other) && isExceptionOf(parent, other, expansion))
              exceptionOf.set(other, parent)

          const seen = new Set(); const next = []
          for (const rule of rules) {
            const sentence = ruleSentence(rule, vocab)
            const body = sentence.complete ? sentence.headline : sentence.structured
            if (!body) continue
            const parent = exceptionOf.get(rule)
            const label = parent ? `${t('desk_restriction_except')} ${body}` : body
            if (seen.has(label)) continue
            seen.add(label)
            next.push({
              label,
              // The parent's tone, so the pair reads as one restriction with a hole in it.
              stance: tone[(parent || rule).polarity] || 'not_allowed',
              ai: false,
              isException: !!parent,
            })
          }
          setItems(next)
        }).catch(() => { if (!gone) setItems([]) })
    }
    load()
    const changed = e => { if (!e.detail?.listingId || e.detail.listingId === listingId) load() }
    window.addEventListener('sentavus:restriction-rules-changed', changed)
    return () => { gone = true; window.removeEventListener('sentavus:restriction-rules-changed', changed) }
  }, [listingId])
  return items
}

// B868 (a): the OWNER'S OWN stated split from the ad ("ვთანამშრომლობ აგენტებთან 50%") as a
// DASHED chip in the cooperation slot — dashed is the reserved "AI-extracted, unconfirmed"
// meaning. Renders only while NO cooperation exists (a negotiated deal outranks a standing
// offer — human wins by absence, B893); clicking opens the same modal the 🤝＋ door opens,
// with the percent prefilled (design (b) — the agent confirms by saving, never auto-written).
// ── The two SHELF verbs: favourite and hide ─────────────────────────────────────────────────
// ONE definition, rendered by the card face (absolute, top-right) and the lineage modal's
// FOOTER. Owner asked for them in the modal 2026-09-10; the footer is where they go because
// this modal has a standing ruling — *the header is for identity, the footer for actions* —
// which is why the share affordance was moved out of the header on 2026-07-25. Favourite and
// hide are verbs, so they sit with contact and share.
//
// ⚠️ THE TWO CONDITIONS STAY SEPARATE, never one combined guard: either shelf can be
// unavailable on its own (the sets arrive from different fetches) and the survivor must still
// render. The card face learned that the hard way and the comment there says so.
//
// ⚠️ `row` must carry address/price/currency, not just an id: toggleFavorite/toggleArchive
// SNAPSHOT those onto the shelf entry when adding, so a shelf row can be drawn before the
// property is fetched. Handing them a bare {property_id} produces a shelf card with no
// address — an entry that looks like a broken save rather than a saved property.
// 🔴 44×44 IS THE TAP TARGET, and the glyph inside it is a separate question (owner-reported
// on mobile 2026-09-10: "these buttons are too small and easy to miss"). They were px-1.5 py-1
// — about 26×22 — which is roughly half the 44px both Apple and Google publish as the minimum,
// and they sit immediately beside the modal's ✕, so a missed tap does not do nothing: it CLOSES
// THE SHEET. That is why this is sized to the guideline rather than nudged up a little.
// ⚠️ The BACKGROUND stays on the padded box, so the lit state still reads as a small pill and
// the corner does not gain two heavy blocks. Growing the target is not the same as growing the
// decoration, and conflating them is what makes people size these by eye and stop short.
// ⚠️ TWO SIZES, AND THE DIFFERENCE IS THE NEIGHBOUR, NOT THE SURFACE (owner 2026-09-10, twice:
// "too small and easy to miss" in the modal, then "still huge buttons" on the card).
// In the MODAL these sit beside ✕, so a missed tap does not do nothing — it CLOSES THE SHEET.
// That is what buys the full 44px guideline. On the CARD the neighbour is the card itself: a
// miss opens the listing, which is where the agent was going anyway, so the same target costs
// real estate on a dense feed and buys much less. 36px keeps a finger honest without the corner
// growing two blocks.
// 📌 My first pass applied 44 to BOTH because the control is shared. Sharing the CONTROL was
// right; sharing its SIZE was not — the cost of a miss is a property of the placement.
const SHELF_HIT = 'min-w-11 min-h-11 flex items-center justify-center'
// 🔴 THE CARD ALREADY HAD ITS TOUCH RULE, AND I DID NOT LOOK (owner: "still huge buttons").
// index.css has carried `@media (max-width:767px){ .desk-card button { min-height:44px } }`
// since 2026-07-24 — every card control is ALREADY 44px on a phone and deliberately small on
// desktop. Adding an explicit min-w-11/min-h-11 made them 44 EVERYWHERE, which is the complaint.
// So the card's variant carries NO minimum of its own: the existing media query owns mobile,
// and desktop goes back to the compact control it always had.
// ⚠️ The MODAL still needs the explicit 44, and that is not an inconsistency: its header is not
// `.desk-card`, so the 2026-07-24 rule never reached it — which is exactly why the owner could
// not hit those buttons in the first place.
// 📌 AND A MEASUREMENT TRAP THAT FOOLED ME: the harness pane can report ZERO width, so
// `max-width:767px` MATCHES and every card button measures 44 — I read that as proof my change
// worked when it was the 2023-era rule answering. Assert the viewport width before trusting any
// measurement taken under a media query.
const SHELF_HIT_COMPACT = 'px-1.5 py-1'
// ── SOLD / RENTED (owner 2026-10-09: "add sold rented button around save and hide … are you sure
// it's rented / sold?"). The SAME market report as the status card's „გაიყიდა / გაქირავდა" radio
// (MarketReportPanel, reason 'deal_done_by_other'), one tap closer. It is a REPORT, not a close:
// unverified, it lowers visibility and suppresses matching, never archives, and the agent can
// retract it in the status card. Our own closed deal (price + fee) stays in that card.
// ⚠️ The confirm is the whole safety: one report already suppresses the property from client
// matches (MARKET_REPORT_SUPPRESS_MIN = 1), so a stray tap on a dense feed must not send it.
const RENT_INTENTS = new Set(['rent', 'daily_rent', 'girao', 'ijara'])
export const goneWord = row => RENT_INTENTS.has(row?.intent) ? 'rented' : 'sold'
export function ShelfToggles({ row, favorites, archived, onToggleFavorite, onToggleArchive, onReportGone = null, reportedGone = 0, className = '', compact = false }) {
  // „არ თანამშ." (owner 2026-10-23 "add alongside გაქირავდა არ თანამშ."): the status card's „არ თანამშრომლობს" report
  // (reason owner_declined_agent), one tap closer - lit once sent from this card
  const [noCoopSent, setNoCoopSent] = useState(false)
  // owner 2026-10-23 "can we not have also designed this confirm window?": the browser's own grey confirm box is replaced by
  // a small Desk-styled question right under the chips - same words, same two answers; nothing is sent until „დიახ"
  const [ask, setAsk] = useState(null)        // null | { text, run }
  const hit = compact ? SHELF_HIT_COMPACT : SHELF_HIT
  const glyph = compact ? 14 : 17
  const pid = row?.property_id
  if (!pid) return null
  const canHide = onToggleArchive && archived
  const canFav  = onToggleFavorite && favorites
  if (!canHide && !canFav && !onReportGone) return null
  const gone = goneWord(row)
  return (
    <span className={`flex items-center gap-1 ${className}`}>
      {ask && (
        <span role="dialog" aria-label={ask.text} onClick={e => e.stopPropagation()}
          className="absolute right-0 top-full mt-1.5 z-30 w-60 rounded-lg border border-border bg-card p-3 text-xs shadow-xl">
          <span className="block text-foreground leading-snug">{ask.text}</span>
          <span className="mt-2.5 flex justify-end gap-2">
            <button type="button" className="rounded-md px-3 py-1.5 text-muted-foreground hover:text-foreground"
              onClick={e => { e.stopPropagation(); setAsk(null) }}>{t('desk_confirm_no')}</button>
            <button type="button" autoFocus className="rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground hover:opacity-90"
              onClick={e => { e.stopPropagation(); const run = ask.run; setAsk(null); run() }}>{t('desk_confirm_yes')}</button>
          </span>
        </span>
      )}
      {onReportGone && (
        <button type="button"
          /* ⚠️ an ACTION, not a status (Desk home audit 2026-10-21): worded "Report sold" and drawn with a dashed edge, so it
             is never read as a closure badge beside "On market" - the recorded closure is a separate solid badge */
          className={`shrink-0 rounded-full border border-dashed border-muted-foreground/50 ${hit} ${compact ? 'text-[11px]' : 'text-xs'} font-medium leading-none transition-colors ${reportedGone
            ? 'bg-amber-500/20 text-amber-500' : 'bg-background/70 text-muted-foreground hover:text-foreground'}`}
          title={t(`desk_gone_${gone}_hint`)}
          onClick={e => {
            e.stopPropagation()
            setAsk({ text: t(`desk_gone_${gone}_confirm`), run: () => onReportGone(row) })
          }}>
          {t(`desk_gone_${gone}`)}
        </button>
      )}
      {onReportGone && (
        <button type="button"
          className={`shrink-0 rounded-full border border-dashed border-muted-foreground/50 ${hit} ${compact ? 'text-[11px]' : 'text-xs'} font-medium leading-none transition-colors ${noCoopSent
            ? 'bg-amber-500/20 text-amber-500' : 'bg-background/70 text-muted-foreground hover:text-foreground'}`}
          title={t('desk_gone_nocoop_hint')}
          onClick={e => {
            e.stopPropagation()
            if (!noCoopSent) setAsk({ text: t('desk_gone_nocoop_confirm'), run: () => { onReportGone(row, 'owner_declined_agent'); setNoCoopSent(true) } })
          }}>
          {t('desk_gone_nocoop')}
        </button>
      )}
      {canHide && (
        <button type="button"
          className={`shrink-0 rounded-full ${hit} leading-none transition-colors ${archived.has(pid)
            ? 'bg-sky-500/20 text-sky-500' : 'bg-background/70 text-muted-foreground hover:text-foreground'}`}
          title={t(archived.has(pid) ? 'desk_archive_remove' : 'desk_archive_add')}
          onClick={e => { e.stopPropagation(); onToggleArchive(row) }}>
          <Icon name="eye-off" size={glyph} />
        </button>
      )}
      {canFav && (
        <button type="button"
          className={`shrink-0 rounded-full ${hit} ${compact ? 'text-sm' : 'text-base'} leading-none transition-colors ${favorites.has(pid)
            ? 'bg-rose-500/20 text-rose-500' : 'bg-background/70 text-muted-foreground hover:text-rose-400'}`}
          title={t(favorites.has(pid) ? 'desk_fav_remove' : 'desk_fav_add')}
          onClick={e => { e.stopPropagation(); onToggleFavorite(row) }}>
          {favorites.has(pid) ? '♥' : '♡'}
        </button>
      )}
    </span>
  )
}
// ── THE OWNER'S OWN WORDS ON THE CHIP (1193, owner 2026-09-16) ─────────────────────────────
// A 🚫 or ⛔ an agent did not set came from the advert, and the agent deciding whether to call
// needs the sentence, not just the glyph. The stance claim (coop_stance_v1) carries the quote.
function stanceMark(stance) {
  if (!stance?.quote || stance.source_kind !== 'ai') return null
  return { agent: 'idmon', version: null, date: stance.observed_at || null, quote: stance.quote }
}
// ⚠️ On a cooperation the mark shows only while the chip still SAYS what the owner said: once an
// agent sets it the other way, the quote is no longer the evidence for what is on screen.
function coopStanceMark(c, offer) {
  const s = offer?.stance
  if (!c || !s) return null
  const agrees = (c.no_cooperation === true && s.no_cooperation === true)
              || (c.no_republish === true && s.no_republish === true)
  return agrees ? stanceMark(s) : null
}
// Worth a chip of its own: a stated split, or a stated refusal. A bare "cooperates" is the default
// and keeps the plain 🤝＋.
const offerShows = (offer) => !!offer && (offer.percent != null
  || offer.stance?.no_cooperation === true || offer.stance?.no_republish === true)

function CoopOfferChip({ offer, onAdd }) {
  if (!offerShows(offer)) return null
  if (offer.percent == null) {
    const s = offer.stance
    const face = [
      s.no_cooperation === true ? `🚫 ${t('desk_coop_no_cooperation')}` : null,
      s.no_republish === true ? `⛔ ${t('desk_coop_no_republish')}` : null,
    ].filter(Boolean).join(' · ')
    return (
      <button type="button"
        title={t(s.no_cooperation === true ? 'desk_coop_no_cooperation_hint' : 'desk_coop_no_republish_hint')}
        onClick={e => { e.stopPropagation(); onAdd && onAdd() }}
        className={pill('add', { door: true, extra: 'text-muted-foreground' })}>
        <Icon name="handshake" /> {face}
        <AiMark mark={stanceMark(s)} />
      </button>
    )
  }
  const mark = { agent: offer.agent || 'idmon', version: offer.prompt_version || null,
                 date: offer.analysed_at || null, quote: offer.quote || null }
  const face = offer.basis === 'price_percent'
    ? `${offer.percent}% ${t('desk_coop_offer_of_price')}`
    : `${offer.percent}%`
  return (
    <button type="button" title={t('desk_coop_offer_tooltip')}
      onClick={e => { e.stopPropagation(); onAdd && onAdd() }}
      className={pill('add', { door: true, extra: 'text-muted-foreground' })}>
      <Icon name="handshake" /> {face}
      {/* The divider used to live HERE, and that was the bug: it fixed this one chip and left
          the equipment row untouched. It now draws itself inside AiMark from :not(:first-child),
          so every surface gets it and none of them has to remember. */}
      <AiMark mark={mark} />
    </button>
  )
}

// Meeting/viewing status → chip color (scheduler `viewings.status`, DealsTab STATUSES).
// DOT variant (PILL_SYSTEM_SPEC §2): in row grammar the status is a 7px dot plus the status
// word at the right edge — a tinted capsule per meeting made five meetings read as five
// alarms. Fill only; the row carries no border of its own.
export const VIEWING_DOT = {
  scheduled: 'bg-blue-500',
  pending:   'bg-muted-foreground/50',
  binder:    'bg-amber-500',
  closed:    'bg-emerald-500',
  failed:    'bg-red-500',
}
export const VIEWING_STATUS_STYLE = {
  scheduled: 'bg-blue-600/20 border-blue-600 text-blue-400',
  pending:   'border-border text-muted-foreground',
  binder:    'bg-amber-600/20 border-amber-600 text-amber-500',
  closed:    'bg-green-600/20 border-green-600 text-green-500',
  failed:    'bg-red-600/20 border-red-600 text-red-400',
}
// CARD-FACE variant (designer note 1, 2026-07-23): tint border/text ONLY — the fill
// stays neutral so a solid status color never out-shouts the price on the card. Modal
// and list surfaces keep VIEWING_STATUS_STYLE above; don't merge the two.
const VIEWING_STATUS_CARD_STYLE = {
  scheduled: 'border-blue-600/60 bg-background text-blue-400',
  pending:   'border-border bg-background text-muted-foreground',
  binder:    'border-amber-600/60 bg-background text-amber-500',
  closed:    'border-green-600/60 bg-background text-green-500',
  failed:    'border-red-600/60 bg-background text-red-400',
}

// Meeting-seed shape of a cooperation's commission agreement. Must carry the SHAPE
// (terms.commission_type, 2026-07-12) — passing commission_percent alone silently
// dropped fixed/owner-net agreements, so ViewingForm fell back to the ad-scraped
// commission as if no agreement existed. One helper, used by every seed site.
// ⚠️ MONEY TRAVELS WITH ITS UNIT AND ITS INTENT (both added 2026-08-15). Three of these
// five fields are amounts and none of them carried a CURRENCY, so the destination invented
// one from the ad's price unit — a cooperation agreed in USD on a GEL-priced listing was
// stored as GEL. And `standard` means "one month's rent per 12-month lease", which is only
// a sentence about a RENT: without the intent the destination applied it to sale ads as
// 100% of the sale price.
export const coopMeetingSeed = c => c ? {
  commission_percent: c.commission_percent,
  min_price: c.min_price,
  min_price_currency: c.min_price_currency || null,
  transaction_intent: c.transaction_intent || null,
  commission_type: c.terms?.commission_type || null,
  owner_net_amount: c.terms?.owner_net_amount ?? null,
  net_commission_amount: c.terms?.net_commission_amount ?? null,
} : null
export const COOP_STATUS_KEYS = { proposed: 'desk_coop_status_proposed', negotiating: 'desk_coop_status_negotiating', agreed: 'desk_coop_status_agreed', rejected: 'desk_coop_status_rejected', ended: 'desk_coop_status_ended' }
export const COOP_INTENT_KEYS = { sale: 'desk_coop_intent_sale', rent: 'desk_coop_intent_rent', girao: 'desk_listing_intent_girao' }
// Listing intent → cooperation transaction_intent (0046 sale|rent, widened to girao by
// 0219). Used to seed a NEW cooperation from the listing it was opened on (2026-07-17,
// user-caught: the 🤝＋ chip on a rent card opened the modal defaulted to SALE — the deal
// type is a fact the listing already states, the agent shouldn't have to re-pick it).
// ⚠️ ijara maps to 'rent', NOT null (corrected 2026-07-27 during the post-girao bug
// sweep). Returning null looked like the careful choice — "let the agent pick" — but
// CooperationModal's state is `useState(seedIntent || 'sale')`, so a null seed doesn't
// produce an empty field, it produces a confidently pre-selected SALE. That is exactly
// the bug fixed for rent cards on 2026-07-17, reappearing the moment a new deal type
// arrived. ijara IS a lease, and it is already treated as rent-shaped everywhere else
// today (termsModeFor → rent panel, DealsTab → rented_out), so 'rent' is both the safe
// default and the consistent one.
// Anything genuinely unmappable still returns null — but fix the CONSUMER before adding
// another such value, because null here means "sale" downstream.
export const coopIntentOf = i => i === 'sale' ? 'sale' : i === 'girao' ? 'girao'
  : (i === 'rent' || i === 'daily_rent' || i === 'lease' || i === 'ijara') ? 'rent' : null

// ─── Cooperation chips — THE one renderer for "cooperations of a property" ───────────────
// Centralize interpretation, distribute rendering: every surface that shows cooperations
// (listing cards, person modal, later matching views) renders THIS component and only passes
// context — no surface re-decides what a cooperation "means" or re-formats its terms.
// `onOpen(c)` opens the edit modal for an existing row; `onAdd` (optional) renders a 🤝+
// affordance for opening a NEW cooperation where the caller knows the contact party.
function coopStatusColor(status) {
  return status === 'agreed' ? 'bg-green-500'
       : status === 'rejected' || status === 'ended' ? 'bg-red-400'
       : 'bg-yellow-400' // proposed / negotiating — in motion
}
function coopAgentNames(c) {
  // participants come embedded from the list endpoint (active only); older cached
  // rows without them just skip the prefix.
  // ⚠️ A withheld identity is null and `filter(Boolean)` DROPPED it, so a cooperation
  // with two agents — one hidden from this viewer — summarised as one name and the reader
  // concluded nobody else was on it. The server redacts rows rather than removing them
  // precisely so the COUNT survives ("rows are redacted, never removed"); discarding the
  // redacted ones here re-created the unknown-as-none failure redaction exists to prevent.
  return (c.participants || [])
    .map(p => p.identity_state === 'withheld' ? t('desk_coop_participant_hidden') : p.party?.display_name)
    .filter(Boolean).join(', ')
}
// The lock must name the action that ACTUALLY unlocks it (BACKLOG B72). The projection
// sends visibility.reason: 'contribute_to_reveal' (you are already a participant — record
// your OWN terms) vs 'not_participant' (join first). Both used to render "join to see
// terms", which is not just vague but wrong for someone who has already joined, and it
// never named the thing that reveals (contributing). Falls back to the join wording when
// the payload predates the reason field.
// ⚠ 🔑/🔒 here stay EMOJI on purpose (2026-07-20 sweep, decision recorded): this and
// coopTermsLabel return STRINGS that flow into `·`-joins and a multi-line title=""
// tooltip (coopChipTooltip's join('\n')) — an <Icon> in that path stringifies to
// [object Object]. They also render mid-sentence at text size, where emoji are
// legible; the migration's blur/tint problems don't apply. Don't "fix" this.
export function coopLockLabel(vis) {
  return `🔑 ${t(vis?.reason === 'contribute_to_reveal' ? 'desk_coop_locked_contribute' : 'desk_coop_locked')}`
}
function coopTermsLabel(c) {
  // Commission token by SHAPE (terms.commission_type, 2026-07-12) — a percent shows as
  // "3%", a flat fee as its amount, an owner's-net as the fee it implies (price − net;
  // only computable when both sides are known), standard as its own word. Formatting
  // only percent used to render net_amount/fixed cooperations as a bare price, which
  // read as "the fee wasn't captured" (Pinar case) when it was in the DB all along.
  const cur = c.min_price_currency || ''
  const ct = c.terms?.commission_type
  const price = Number(c.min_price)
  const commission =
    // ⚠️ coop_split FIRST, before the percent branch — it stores its number in the SAME
    // commission_percent column and that branch would print „50% ≈ 100,000 USD" on a
    // 200,000 flat, which is the arithmetic the type exists to stop (owner 2026-09-12).
    // The share is shown as a share and no ≈ is offered: the fee it is a share OF is not
    // recorded here.
    ct === 'coop_split' && c.commission_percent != null
      ? `${c.commission_percent}% ${t('desk_coop_type_coop_split')}`
    : c.commission_percent != null
      ? (price > 0 ? `${c.commission_percent}% ≈ ${Math.round(price * c.commission_percent / 100).toLocaleString()} ${cur}`.trim()
                   : `${c.commission_percent}%`)
    : (ct === 'net_commission' || ct === 'fixed_net') && c.terms?.net_commission_amount != null
      ? `${Number(c.terms.net_commission_amount).toLocaleString()} ${cur}`.trim()
    : ct === 'net_amount' && c.terms?.owner_net_amount != null
      ? (price > 0 ? `${(price - Number(c.terms.owner_net_amount)).toLocaleString()} ${cur}`.trim()
                   : `${t('desk_coop_owner_net_label')} ${Number(c.terms.owner_net_amount).toLocaleString()} ${cur}`.trim())
    : ct === 'standard' ? t('desk_coop_type_standard')
    // Numbers gated for this viewer (Track 2 visibility): terms exist but are earned.
    : (c.visibility && c.visibility.can_see_numbers === false) ? coopLockLabel(c.visibility)
    : null
  return [
    t(COOP_INTENT_KEYS[c.transaction_intent]) || c.transaction_intent,
    commission,
    // Price only when it's the sole number — once a fee token exists, the chip shows
    // what the AGENT takes, not the deal size (2026-07-12, agent-directed). A zero
    // min-price is "not negotiated", not a term worth a chip token either way.
    !commission && price > 0 ? `${price.toLocaleString()} ${cur}`.trim() : null,
    // Exclusivity is chip-worthy (2026-07-12, agent-directed): it changes how every
    // other agent should treat the property. Expiry rides along when set.
    c.exclusive ? `🔒 ${t('desk_coop_exclusive')}${c.valid_until ? ` → ${new Date(c.valid_until).toLocaleDateString()}` : ''}` : null,
    // 1073 — chip-worthy for the same reason exclusivity is: it changes what another
    // agent may DO with this property, and an agent about to repost it needs the fact
    // before they open the modal, not inside it. Only `true` renders — the column is
    // three-state and a null (nobody has asked the owner) must show nothing at all.
    c.no_republish === true ? `⛔ ${t('desk_coop_no_republish')}` : null,
    // 1080 — its own token, never folded into the one above: an agent reading „⛔ არ
    // გააზიაროთ" learns not to repost, and „🚫 არ თანამშრომლობს" that there is no
    // commission to be had here at all. Both can appear.
    c.no_cooperation === true ? `🚫 ${t('desk_coop_no_cooperation')}` : null,
  ].filter(Boolean).join(' · ')
}
// WHOSE deal leads. The name-gating here was removed 2026-07-20 (BACKLOG B71): WHICH
// agents participate is AGENCY knowledge — VISIBILITY.md §cooperation lists "participant
// list (which agents) | A" — and the lineage modal's CooperationList already shows every
// name to everyone. Gating it on the chip meant the same fact had two answers depending
// on which surface you looked at (KNOWLEDGE_MODEL §0), and it was UI-only hiding anyway:
// the names were already in `c.participants` in the props, just not rendered. The NUMBERS
// stay server-gated (coopTermsLabel renders 🔑 when the projection withheld them).
function coopChipLabel(c) {
  return [coopAgentNames(c) || null, coopTermsLabel(c)].filter(Boolean).join(' · ')
}
// bestCooperation moved to lib/coopMath.js (2026-08-01) — the meeting card and the meeting
// modal need it too, and importing it back from a component that already imports
// MeetingsFeed would close an import cycle. Its B159 rationale travelled with it.
function coopHoverLines(coops) {
  return coops.map(c => [
    coopAgentNames(c) || '—',          // agency knowledge (B71) — see coopChipLabel
    coopTermsLabel(c),
    t(COOP_STATUS_KEYS[c.status]) || c.status,
  ].filter(Boolean).join(' · ')).join('\n')
}
// Meeting scheduled time — latest scheduled/reschedule event (events arrive newest-first
// from _assembleViewing); rows from getViewingsBySourceUrls carry a precomputed
// scheduled_at instead of embedded events. Null = meeting exists but no time set yet.
export function viewingSchedAt(v) {
  const ev = (v.events || []).find(e => e.kind === 'scheduled' || e.kind === 'reschedule')
  const iso = ev?.event_at || v.scheduled_at || null
  return iso ? new Date(iso).getTime() : null
}
// ⚠️ TBILISI, NEVER THE DEVICE (owner ruling 2026-08-12: "meeting calculates from devices
// time and thats wrong"). The Meetings feed and the modal were converted then; THIS file was
// missed, so the same meeting printed one hour on the feed and another on the property
// card's chip — and across midnight, a different DAY. See src/lib/tbilisiTime.js.
export function viewingSchedText(at) {
  return at ? fmtTbilisi(new Date(at).toISOString(), { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : null
}
// Card-face ordering: the next thing to HAPPEN first (soonest upcoming), then history
// newest-first. The face chip is just this list's head — one rule, so the chip and the
// list it opens can never disagree about which meeting leads.
function orderMeetings(meetings) {
  const now = Date.now()
  const withAt = meetings.map(v => ({ v, at: viewingSchedAt(v) }))
  const upcoming = withAt.filter(x => x.at != null && x.at >= now).sort((a, b) => a.at - b.at)
  const rest = withAt.filter(x => x.at == null || x.at < now).sort((a, b) => (b.at || 0) - (a.at || 0))
  return [...upcoming, ...rest]
}
// The face card shows ONE meeting chip — the closest upcoming (else the most recent) —
// with every meeting in the hover.
function pickClosestMeeting(meetings) { return orderMeetings(meetings)[0] }
// The representing agent(s) as an agency-wide COORDINATION fact — "who's already taking
// a client here, coordinate with them" — NOT ownership of the client (client name/phone
// stay host-only, nulled server-side, Track 2). Everyone sees the name now; on someone
// else's meeting it reads "Represented by X", while your own meeting or a management
// view shows the name plainly. v.hosted_by_me is server-computed per row (true for the
// hosting agent AND for management). Empty → null so the segment is simply omitted.
export function representingSeg(v, privileged) {
  const who = (v.agent_names || []).filter(Boolean).join(', ')
  if (!who) return null
  return (privileged || v.hosted_by_me) ? who : t('desk_represented_by', { name: who })
}
function meetingChipLabel(v, at, privileged) {
  return [
    representingSeg(v, privileged),
    viewingSchedText(at),
    v.client_name || null,
    t(MEETING_STATUS_KEYS[v.status]) ? t(MEETING_STATUS_KEYS[v.status]) : v.status,
  ].filter(Boolean).join(' · ')
}
function meetingHoverLines(meetings, privileged) {
  return meetings.map(v => meetingChipLabel(v, viewingSchedAt(v), privileged)).join('\n')
}
// ─── Meeting chip (A→C 2.3, ruling §4e) — ONE chip, the whole state machine ───────────────
// no meeting        → no chip (the bare 📅＋ in the row below IS the affordance)
// active meeting(s) → nearest ACTIVE, tinted on border/text only (VIEWING_STATUS_CARD_STYLE)
// finished only     → muted GREY history "📅 ×4 · ბოლო: ჩაიშალა", NEVER the failed-red:
//                     history doesn't shout, red on a card face reads "act now"
// ⚠️ With MORE than one meeting the click opens a LIST, not the closest one (2026-07-24).
// It used to open pickClosestMeeting's editor unconditionally, so on a property with four
// viewings the other three were unreachable from the card face — the ×N announced they
// existed and then gave you no way in. The hover still carries every line for scanning.
function MeetingChip({ meetings, privileged, onOpenMeeting }) {
  const [pos, setPos] = useState(null)   // fixed-viewport coords for the portal; null = closed
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  // Same portal + fixed-position idiom as ContactPill, and for the same reason: a collapsed
  // stack card wraps its front card in a z-indexed, position:relative box, so an in-tree
  // popover is trapped in that stacking context and can never paint above the NEXT card in
  // the grid, however high its z-index. Close on scroll/resize (cheaper than re-measuring)
  // and on any outside click in the capture phase (the card's own onClick stops propagation).
  // Scroll/resize live in useCloseOnPageMove — a scroll INSIDE this panel is the agent
  // reading it, not the page moving under it.
  useCloseOnPageMove(!!pos, () => setPos(null), panelRef)
  useEffect(() => {
    if (!pos) return
    const onDocClick = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      setPos(null)
    }
    document.addEventListener('click', onDocClick, true)
    return () => {
      document.removeEventListener('click', onDocClick, true)
    }
  }, [pos])
  const active = meetings.filter(v => MEETING_ACTIVE.includes(v.status))
  const many = meetings.length > 1
  const pick = pickClosestMeeting(active.length ? active : meetings)
  const hover = meetingHoverLines(meetings, privileged)
  return (<>
    <button ref={btnRef} title={hover}
      onClick={e => {
        e.stopPropagation()
        if (!many) { onOpenMeeting?.(pick.v); return }
        const r = btnRef.current.getBoundingClientRect()
        // Clamp to the viewport: the chip sits 3rd–4th in the action row, so on a 390px
        // phone its left edge is well past 70px and an unclamped panel (320px fixed) would
        // hang off the right side with its rows unreadable.
        setPos(p => p ? null : { top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - 328)) })
      }}
      className={pill('none', { door: true, extra: active.length
        ? (VIEWING_STATUS_CARD_STYLE[pick.v.status] || 'border-border bg-background text-muted-foreground')
        : 'border-border bg-background text-muted-foreground' })}>
      {/* ×N keeps the 📅 icon so a collapsed count stays distinguishable (designer note 4).
          The history variant leads with the count — "×4 · last: fell through" — while the
          active variant leads with the meeting itself and trails the count. */}
      <Icon name="calendar" /> {active.length
        ? <>{meetingChipLabel(pick.v, pick.at, privileged)}{many ? <span className="opacity-70">×{meetings.length}</span> : null}</>
        : <>×{meetings.length} · {tLog('desk_meeting_last_outcome', { status: t(MEETING_STATUS_KEYS[pick.v.status]) || pick.v.status })}</>}
    </button>
    {/* `overflow-y-auto` pairs with the maxHeight `pos` carries — without it a long meetings
        list is CLIPPED at that height with no way to reach the rest. */}
    {pos && createPortal(
      <div ref={panelRef} className="desk-popover fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-1.5 px-3 w-[320px] text-sm overflow-y-auto overscroll-contain"
        style={pos} onClick={e => e.stopPropagation()}>
        <p className="text-xs text-muted-foreground pb-1 border-b border-border/40">{t('desk_meeting_list_title')}</p>
        {orderMeetings(meetings).map(({ v, at }) => (
          <button key={v.id}
            className="block w-full text-left py-1.5 text-xs border-b border-border/40 last:border-0 hover:text-primary"
            onClick={() => { setPos(null); onOpenMeeting?.(v) }}>
            {meetingChipLabel(v, at, privileged)}
          </button>
        ))}
      </div>,
      document.body
    )}
  </>)
}
// Modal-grade LIST view (2026-07-20, user-directed: "like გაყიდვის პირობები — names and
// values, list view"): one row per PARTICIPANT with their own recorded terms, instead of
// the one-line chip (which stays on card faces where space is scarce). Participant NAMES
// are an agency fact (VISIBILITY.md §cooperation) — every viewer sees WHO cooperates;
// the VALUES arrive already projected by the server (nulled unless earned), so this
// renders 🔑 where a number was withheld and never re-derives visibility client-side.
function coopParticipantValue(c, p) {
  const cur = p.min_price_currency || c.min_price_currency || ''
  const bits = []
  if (p.commission_percent != null) bits.push(`${p.commission_percent}%`)
  if (p.min_price != null && Number(p.min_price) > 0) bits.push(`${Number(p.min_price).toLocaleString()} ${cur}`.trim())
  if (bits.length) return bits.join(' · ')
  // No numbers on the row: either the projection withheld them (🔑 — hidden, not absent,
  // §KM 1.4) or this participant simply recorded none, which the bare ＋ affordance says
  // better than any words (same convention as the sale-terms card).
  return c.visibility?.can_see_numbers === false && p.contributed ? coopLockLabel(c.visibility) : null
}
// One row = one agent, in the SAME visual language as the sale-terms card above it
// (2026-07-20): name · action. A row with nothing recorded shows a dashed pill with ＋ and
// no words — absence is the message, exactly as 🏠＋ means it there.
//
// ⚠️ PRESENCE RULE (owner ruling 2026-07-25, CARD_FOOTER_SPEC.md §1) — a dashed affordance
// exists to represent ABSENCE. Once a value is recorded, the value chip IS the button and
// the 🤝 pill must not also render: both opened the same modal, so the row carried two
// buttons for one action, and the redundant pill read as "something still to add" on a row
// that was already complete (owner-flagged, screenshot: "Luka Bigvava 🤝 [418 USD]").
// A withheld value (🔑 lock) still counts as a value — clicking it is exactly how you
// contribute your own terms to reveal theirs — so it takes the same value-as-button path.
// ⚠️ NOW A ROW, not a capsule (PILL_SYSTEM_SPEC §2, 2026-07-30). This shape renders once per
// AGENT, so N agents produced N identical bordered capsules — six amber pills stacked down the
// column, carrying no more information than six rows and shouting six times as loud. Row
// grammar (name left, value right, hairline between) is what the left specs column already
// uses, and it is the same grammar the sale-terms card needs.
//
// The PRESENCE RULE survives the change, restated: the ROW is the door. Previously "the value
// chip IS the button, and the 🤝 pill must not also render alongside it" — one door per row,
// never two for one action. A withheld value (🔑 lock) still counts as a value, because
// clicking it is exactly how you contribute your own terms to reveal theirs.
// readOnly = a COLLEAGUE's row: a fact, never a door (an agent joins themselves — a pressable
// affordance on someone else's row was a door into someone else's relationship).
function CoopAgentRow({ name, value, recordedAt = null, joined, onClick, title, readOnly = false }) {
  const body = (
    <>
      <span className={`min-w-0 flex items-baseline gap-2 ${joined ? '' : 'text-muted-foreground/60'}`}>
        <span className={joined
          ? ROW_LEFT
          : 'min-w-0 truncate text-[13.5px] leading-[1.45]'}>{name}</span>
        {recordedAt && <span className="shrink-0 text-xs font-normal text-muted-foreground">{timeAgo(recordedAt)}</span>}
      </span>
      {value
        ? <span className={ROW_VAL}>{value}</span>
        : readOnly
          // An empty colleague row states the absence in words rather than offering a
          // control that isn't theirs to press.
          ? <span className={`${ROW_VAL} opacity-70`}>{t('desk_coop_no_terms_yet')}</span>
          // The ONE dashed action, and it names its verb (§2: never a bare ＋).
          : <span className={pill('add')}><Icon name="handshake" /> {t('desk_coop_join_action')}</span>}
    </>
  )
  return readOnly
    ? <div className={ROW}>{body}</div>
    : <button type="button" title={title} onClick={onClick}
        className={`${ROW} w-full text-left rounded-md hover:bg-accent/40`}>{body}</button>
}
export function CooperationList({ cooperations, onOpen, onAdd, candidates = [], addContactName = null, viewerName = null }) {
  // Read-time unification (2026-07-29, KNOWLEDGE_MODEL: "reads unify on normalized
  // phone membership"): duplicate contact-party rows / sibling property rows split one
  // real relationship into several cooperation rows. The server ships contact_key (the
  // canonical contact id per phone membership); rows sharing key+intent render as ONE
  // group — an owned, labeled collapse: every underlying row keeps its own participant
  // rows and stays openable, and a merged group says how many records it holds.
  const raw = cooperations || []
  const groups = new Map()
  for (const c of raw) {
    const k = `${c.contact_key || c.contact_party_id || c.id}|${c.transaction_intent}`
    if (!groups.has(k)) groups.set(k, [])
    groups.get(k).push(c)
  }
  // LIVE record leads the group: its status/terms are the relationship's current
  // truth — ended duplicates (merge history) stay as members, never as the face.
  const isLive = c => !['ended', 'rejected'].includes(c.status)
  for (const rows of groups.values()) rows.sort((a, b) => (isLive(b) ? 1 : 0) - (isLive(a) ? 1 : 0))
  const coops = [...groups.values()].map(rows => rows[0])
  // Agents WORKING the property (they have a posting) but not yet in any cooperation —
  // the "who hasn't set their terms yet" tier (user-directed 2026-07-20). Matched by
  // display name (posting author_name and participant party.display_name are the same
  // party's name) — a display-level nicety, not an identity claim.
  const inCoop = new Set(raw.flatMap(c => (c.participants || []).map(p => p.party?.display_name).filter(Boolean)))
  // ⚠️ FAIL CLOSED once any identity is withheld. `pending` asserts "this agent has a
  // posting and is in NO cooperation" — exactly what cannot be known when the name we would
  // match against was redacted. Asserting it anyway tells the viewer a colleague has not
  // recorded terms when they may well have.
  const anyHidden = raw.some(c => (c.participants || []).some(p => p.identity_state === 'withheld'))
  const pending = (coops.length && !anyHidden) ? [...new Set(candidates)].filter(n => n && !inCoop.has(n)) : []
  return (
    <div className="space-y-2">
      {coops.map(c => {
        const members = groups.get(`${c.contact_key || c.contact_party_id || c.id}|${c.transaction_intent}`) || [c]
        return (
        <div key={c.id} className="space-y-1">
          {/* Header — the whole block opens the modal. §2/§3b(8): the status was a bare 1.5px
              coloured dot with the status WORD buried at the end of a three-part join, so the
              dot was unexplained and the word was unfindable. Now the status is the card's ONE
              labelled state pill on the right, and the dot is gone — it said nothing the pill
              doesn't say in words. */}
          <button onClick={() => onOpen(c)}
            className="w-full flex items-start justify-between gap-3 text-left hover:opacity-80">
            <span className="min-w-0">
              {/* The OWNER leads the line (owner ruling 2026-07-29): every relationship this
                  card records is agent↔owner — a header without the counterparty read as an
                  intra-agency roster. Name comes via with_contact (agency fact, never the
                  phone). coopTermsLabel = intent · commission/price (incl. 🔑) · exclusive */}
              {/* 🔴 THE NOUN IS NOT ALWAYS "OWNER" (owner-caught 2026-09-09: "on top it still
                  says მესაკუთრე even though below it shows agency"). It was hard-coded here
                  from 2026-07-29 on the belief that every cooperation is agent↔owner; the
                  reported card was with Xide Partners, an agency, and the contact list two
                  panels down said so — one screen, two answers about one party.
                  `contact_is_agent` comes from the SAME roster + agency-dictionary pair that
                  labels the contact row, so the two cannot drift. Absent (an older payload,
                  or the soft-failed dictionary probe) it is falsy and the label is exactly
                  what it has always been. */}
              <span className={`${ROW_LEFT} block`}
                title={c.contact_agency || undefined}>
                {(() => {
                  const noun = t(c.contact_is_agent ? 'desk_role_agent' : 'desk_role_owner')
                  const nm = c.contact?.display_name || c.contact?.name
                  return nm ? `${nm} (${noun})` : noun
                })()}
                {/* labeled collapse: the group unifies N recorded rows (duplicate contact
                    rows / sibling ads of one flat) — say so rather than hide it. */}
                {members.length > 1 && <span className="ml-1 opacity-60" title={t('desk_coop_group_note')}>×{members.length}</span>}
              </span>
              <span className="block text-xs leading-[1.45] text-muted-foreground truncate">{coopTermsLabel(c)}</span>
            </span>
            <span className={pill(c.status === 'agreed' ? 'ok'
              : (c.status === 'rejected' || c.status === 'ended') ? 'warn' : 'neutral', { sm: true })}>
              {t(COOP_STATUS_KEYS[c.status]) || c.status}
            </span>
          </button>
          <div className="space-y-1 pl-3">
            {/* ONE row per person: merged duplicates carry copies of the same
                participant (the sweep moved rows onto the survivor and kept the
                originals on the ended records) — the live record's row wins. */}
            {(() => {
              const seen = new Set()
              return members.flatMap(m => (m.participants || []).map(p => ({ m, p })))
                // ⚠️ A withheld identity carries party_id null, so keying the dedup on it
                // collapsed EVERY hidden participant into ONE row and changed the count — the
                // one thing the redaction is careful to preserve. Hidden rows are never
                // deduped: they are indistinguishable by definition, so "same key" here
                // means "unknown", not "same person".
                .filter(({ p }) => p.identity_state === 'withheld' ? true
                  : seen.has(p.party_id) ? false : (seen.add(p.party_id), true))
            })().map(({ m, p }, i) => (
              <CoopAgentRow key={`${m.id}-${p.party_id || `hidden-${i}`}`}
                name={p.identity_state === 'withheld' ? t('desk_coop_participant_hidden') : (p.party?.display_name || '—')}
                value={coopParticipantValue(m, p)} joined
                recordedAt={p.terms_updated_at || p.last_confirmed_at || p.joined_at || null}
                title={tLog('desk_coop_chip_tooltip', { status: t(COOP_STATUS_KEYS[m.status]) || m.status })}
                onClick={() => onOpen(m)} />
            ))}
            {/* Posting agents not yet in the cooperation. Same row shape, ＋ on the pill and
                a dimmed name — no "not set yet" caption: the sale-terms card says the same
                thing with a bare 🏠＋, and four repetitions of a sentence is noise. The
                affordance opens the cooperation (its participants editor is where you join). */}
            {/* Join affordance only on the VIEWER's own pending row (owner ruling
                2026-07-29): an agent joins THEMSELVES — a pressable ＋ on a colleague's
                row was a door to someone else's relationship. Colleagues' pending rows
                stay as read-only facts ("works this property, no terms yet"). */}
            {c === coops[0] && pending.map(n => (
              <CoopAgentRow key={`pending-${n}`} name={n} value={null} joined={false}
                readOnly={!viewerName || n !== viewerName}
                title={t('desk_coop_not_joined')} onClick={() => onOpen(c)} />
            ))}
          </div>
        </div>
        )
      })}
      {/* The create affordance belongs to the EMPTY state only (2026-07-20). Once a
          cooperation exists every row carries its own pill, so a trailing ＋ is a second
          button for the same action — open_cooperation is find-or-create on
          property+contact+intent, so it would have opened that very row anyway. The
          sale-terms card has no trailing button either; rows ARE the affordance. */}
      {onAdd && !coops.length && (
        <button title={t('desk_coop_open_tooltip')} onClick={onAdd}
          className={pill('add', { door: true })}>
          {/* Name WHO the cooperation would be with (2026-07-29) — a bare 🤝＋ left the
              counterparty implicit, and the card read as agents cooperating with each other. */}
          <Icon name="handshake" />＋{addContactName ? ` ${addContactName} (${t('desk_role_owner')})` : null}
        </button>
      )}
    </div>
  )
}
// ─── Card footer primitives (CARD_FOOTER_SPEC.md §4) ─────────────────────────────────────
// Hover CAPABILITY decides the interaction, not viewport width. A touch screen has no hover
// state to preview with, so it gets tap→detail→Edit; a mouse gets hover→detail, click→edit.
// A width breakpoint would hand a touch laptop the desktop contract and be wrong about it.
// useHoverCapable moved to primitives.jsx (2026-07-30) — the card-face NearbyHover needs
// the same touch/hover test, and two copies of "does this device hover" would drift.

const FACET_PANEL_W = 320
// Portal + fixed position, for the same reason ContactPill / MeetingChip / EmojiSelect do
// it: a collapsed stack card wraps its front card in a z-indexed, position:relative box, so
// an in-tree popover is trapped in that stacking context and can never paint above the NEXT
// card in the grid, however high its z-index. Placement (prefers UPWARD) is popoverPos —
// one rule, shared with ShareMenu; see its comment in primitives.jsx.
const facetPos = el => popoverPos(el, FACET_PANEL_W)

// ONE chip contract for every card-footer facet (cooperation, meeting, restriction).
// `rows` is the detail view: what the face had to drop to become a single token (§2/§3).
// ⚠️ `title=` is NOT a substitute for `rows` — tooltips do not exist on a touch screen, and
// before this the whole payload lived there, i.e. it was unreachable on the phone the Desk
// is actually reviewed on. The tooltip stays as a mouse convenience; `rows` is the contract.
function FacetChip({ icon, children, cls, style, tooltip, detailTitle, rows = [], onEdit, editLabel, editOnClick = false }) {
  const hoverCapable = useHoverCapable()
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const closeTimer = useRef(null)
  const open = () => setPos(facetPos(btnRef.current))
  const close = () => setPos(null)
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null } }
  // Leaving the button must not kill a panel the pointer is travelling INTO — the 120ms
  // grace is what makes a multi-row detail list clickable with a mouse.
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(close, 120) }

  useCloseOnPageMove(!!pos, close, panelRef)
  useEffect(() => {
    if (!pos) return
    const onDocClick = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      close()
    }
    document.addEventListener('click', onDocClick, true)
    return () => {
      document.removeEventListener('click', onDocClick, true)
      cancelClose()
    }
  }, [pos])

  // §4. Mouse: click goes straight to the editor, EXCEPT when the detail is a list of
  // several rows — then the click opens the list, because "which one did you mean" has no
  // answer (the 2026-07-24 reachability fix: a card with four meetings offered no way into
  // three of them). Touch: click always opens the detail; the Edit button inside is the
  // only path to the editor, so an editor is never entered by accident.
  // `editOnClick` (2026-08-03) opts a chip OUT of the list-like rule above: press always goes
  // to the editor, on mouse AND touch, and the popover stays a hover-only preview. The 🔗
  // links chip needs it — its editor IS the list (ShareModal shows every link with open /
  // copy / replace / revoke on each), so routing a click to a read-only popover would strand
  // the agent one hop short of every action the rows describe.
  const listLike = rows.length > 1 && !editOnClick
  const onClick = e => {
    e.stopPropagation()
    if (editOnClick && onEdit) { close(); onEdit(); return }
    if (!hoverCapable) { pos ? close() : open(); return }
    if (listLike) { pos ? close() : open(); return }
    onEdit?.()
  }
  return (<>
    <button ref={btnRef} type="button" title={tooltip} style={style} onClick={onClick}
      onMouseEnter={hoverCapable ? () => { cancelClose(); open() } : undefined}
      onMouseLeave={hoverCapable ? scheduleClose : undefined}
      className={cls}>
      {icon}{children}
    </button>
    {pos && createPortal(
      <div ref={panelRef} style={pos}
        onMouseEnter={hoverCapable ? cancelClose : undefined}
        onMouseLeave={hoverCapable ? scheduleClose : undefined}
        onClick={e => e.stopPropagation()}
        className="desk-popover fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-1.5 px-3 w-[320px] text-sm overflow-y-auto overscroll-contain">
        {detailTitle && <p className="text-xs text-muted-foreground pb-1 border-b border-border/40">{detailTitle}</p>}
        {rows.map(r => (
          r.onOpen
            ? <button key={r.key} type="button"
                /* `accent` marks a row that CREATES rather than opens (the meeting chip's
                   ＋ door). It reads as an action, not as one more record in the list. */
                className={`block w-full text-left py-1.5 text-xs border-b border-border/40 last:border-0 ${
                  r.accent ? 'text-primary font-medium hover:opacity-80' : 'hover:text-primary'}`}
                onClick={() => { close(); r.onOpen() }}>{r.label}</button>
            : <p key={r.key} className="py-1.5 text-xs border-b border-border/40 last:border-0">{r.label}</p>
        ))}
        {/* Touch only: the face tap showed this view, so the editor needs its own door. With
            a mouse the click already went there, and a second button would be a dead limb. */}
        {!hoverCapable && onEdit && (
          <button type="button" onClick={() => { close(); onEdit() }}
            className="mt-1 w-full text-center py-1.5 text-xs rounded-md border border-border hover:border-primary/50 hover:text-foreground text-muted-foreground">
            {editLabel}
          </button>
        )}
      </div>,
      document.body
    )}
  </>)
}

// §2 — the cooperation FACE is the fee, as money, and nothing else. Names, intent, the raw
// %, exclusivity and the status word all move into the detail rows. Kept separate from
// coopTermsLabel (which modals still render in full) so the two can't drift into each other.
function coopFaceLabel(c) {
  const cur = c.min_price_currency || ''
  const ct = c.terms?.commission_type
  const price = Number(c.min_price)
  // coop_split FIRST, exactly as coopTermsLabel does (review 2026-09-23): it stores its share in
  // the same commission_percent column, so the branch below drew a 50% split on a 200,000 flat
  // as "100,000 USD" — the arithmetic the type exists to stop (owner 2026-09-12).
  if (ct === 'coop_split' && c.commission_percent != null)
    return `${c.commission_percent}% ${t('desk_coop_type_coop_split')}`
  if (c.commission_percent != null) {
    // A percent shows as the MONEY it implies — "12,540 USD", not "3% ≈ 12,540 USD". With no
    // price to apply it to, the percent is the only fact there is, so it stands alone.
    return price > 0
      ? `${Math.round(price * c.commission_percent / 100).toLocaleString()} ${cur}`.trim()
      : `${c.commission_percent}%`
  }
  if ((ct === 'net_commission' || ct === 'fixed_net') && c.terms?.net_commission_amount != null)
    return `${Number(c.terms.net_commission_amount).toLocaleString()} ${cur}`.trim()
  if (ct === 'net_amount' && c.terms?.owner_net_amount != null && price > 0)
    return `${(price - Number(c.terms.owner_net_amount)).toLocaleString()} ${cur}`.trim()
  if (ct === 'standard') return t('desk_coop_type_standard')
  // ⚠️ Gated viewer (§2, load-bearing — NOT an edge case): the server withheld the numbers,
  // so the face is the lock GLYPH alone. Which action unlocks it is a sentence, and a
  // sentence is not a token — coopLockLabel's wording belongs in the detail rows below.
  if (c.visibility && c.visibility.can_see_numbers === false) return '🔑'
  if (price > 0) return `${price.toLocaleString()} ${cur}`.trim()
  return null   // the cooperation exists but records no number yet — a bare 🤝 says that
}

// ─── Client links (🔗, owner request 2026-08-03) ─────────────────────────────────────────
// "After the share pill, a link pill — it must show links created for clients with price and
// seen information. All the links the agent created must be there."
//
// A share is a COMMUNICATION ARTIFACT: its price was frozen at mint (MEETINGS_MODEL §5), so
// these numbers are what a specific client was actually told, NOT the card's live price. That
// is exactly why they belong on the face — an agent looking at a card needs "I already sent
// this to two people at 450, and one of them opened it three times" before quoting a third.
//
// Fill-state, same as every other footer group (owner 2026-08-03: "when there is no links the
// circle must be dotted; when there is links it must be full like all the other pills"):
// dashed ＋ door = no link has been sent, solid chip = links exist. So the pill is ALWAYS
// present and its outline alone answers "did I already send this?" from across the card.
// ⚠️ Both states open the SAME ShareModal that ↗'s "🔗 to client" opens — two doors, one room,
// exactly like 🤝/🏠. What must never happen is a second minting MECHANISM: this hands off to
// that modal and never writes a share itself.
// ⚠️ It shows MY links. The endpoint scopes to sender_party_id unless the viewer is
// management, so the count is "what I sent", never "what the agency sent" — a card must not
// imply an agent knows about a colleague's client.
const shareIsDead = s => !!(s.revoked_at || s.expired_at)
// The frozen price a given link carries, as money. A link minted with no price ("price on
// request") is a real state, not a gap. Used by the ROWS only — never by the chip face, which
// carries the glyph and the count and nothing else (see the face comment below).
function shareFaceLabel(s) {
  if (!s) return null
  return s.offered_price != null
    ? `${Number(s.offered_price).toLocaleString('en-US')} ${s.offered_currency || ''}`.trim()
    : t('share_price_on_request')
}
// The dot: has ANYONE opened a link for this property. Green once something came back,
// muted while every link is still unopened — the same "status is a colour, detail is a row"
// split the cooperation chip uses. Never a number: which link, how often and when are facts
// with a shape, and a shape does not fit in 6px.
const shareSeenColor = shares => shares.some(s => s.engagement?.meeting_requested) ? 'bg-emerald-500'
  : shares.some(s => (s.engagement?.opens || 0) > 0) ? 'bg-sky-500'
    : 'bg-muted-foreground/50'
// One row per link: what they were quoted, when it went out, and what they did with it.
// Deliberately the SAME sentence the ShareModal's history renders — one link, one reading,
// whichever surface the agent happens to be on.
function shareRowLabel(s) {
  const e = s.engagement || {}
  return [
    shareFaceLabel(s),
    timeAgo(s.created_at),
    shareIsDead(s) ? t('share_revoked')
      : e.meeting_requested ? t('share_requested_viewing')
        : e.opens ? [tLog('share_opens', { n: e.opens }),
          e.last_opened_at ? tLog('share_last_viewed', { when: timeAgo(e.last_opened_at) }) : null]
          .filter(Boolean).join(' · ')
          : t('share_not_opened'),
  ].filter(Boolean).join(' · ')
}
// The same row with the agent's private memo ON TOP (0379) — who this link went to, which is
// the question the chip exists to answer; the price is the second question. FacetChip renders
// a row's `label` as a node, so this is the ONE row shape both surfaces share: the memo line
// here matches the memo pill in ShareModal's history, down to the amber and the lock.
// ⚠️ The lock is the Icon (SVG), not a 🔒 character. Screen renders use the icon component;
// the emoji form belongs to outbound text only — which is why `tooltip` below, a plain title
// attribute, is the one place that keeps the character.
function ShareRow({ s }) {
  return (
    <span className="block">
      {s.sender_memo && (
        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-medium">
          <Icon name="lock" size={11} />
          <span className="truncate">{s.sender_memo}</span>
        </span>
      )}
      <span className="block">{shareRowLabel(s)}</span>
    </span>
  )
}
// Plain-text form for the chip's hover `title` — same facts, one line, memo first. Outbound
// text, so the lock is a character here (see the note above).
const shareRowText = s => (s.sender_memo ? `🔒 ${s.sender_memo} · ` : '') + shareRowLabel(s)

// ── 🔗 THE client-links chip — ONE renderer, every surface ──────────────────
// Extracted from CardFooter's rail 2026-08-04 so the lineage modal's footer can
// render the SAME chip instead of growing a second one. Exactly the precedent
// ShareMenu and ContactPill already set two lines below ("THE one share
// affordance: the lineage modal's footer renders this same component") — a card
// and the modal you open FROM that card must not disagree about how many links
// were sent or what they say.
//
// Dashed ＋ when none exist, solid chip when they do.
//
// ⚠️ `shares == null` MEANS "NOT LOOKED YET" AND RENDERS NOTHING — it is not the
// same as `[]` ("looked, none sent"). The distinction is the one IntakeTab's
// sharesByProperty comment already draws: a chip that renders the dashed ＋
// before its fetch lands tells the agent "no links sent" about a property that
// may well have several, and it says it in the split-second before the truth
// arrives. Absent beats wrong.
// ── THE LINKS PILL (owner 2026-09-08) ────────────────────────────────────────────────────
// "links will have share link to agent, share link to client, and when there are already
// shared will show history as it does right now."
//
// The two link KINDS moved here out of ↗ (ShareMenu), and the reason they are two named
// choices rather than one "share" is unchanged and still load-bearing: sending a CLIENT the
// agent link exposes the internal workbench, and sending a COLLEAGUE the client link hands
// them a frozen price and burns a share token. They are not interchangeable, so the menu
// makes the choice by name, once.
//
// ⚠️ THE FACE STILL ANSWERS "have I already sent one?" — that is why the history lives on
// this pill and not the other: it is the answer to the question the links raise. Rows
// present → the FacetChip with ×N and the sent list; none → the dashed 🔗＋.
// ⚠️ `shares == null` is NOT LOADED and renders nothing, never a false "none sent".
// 📍 SHARE LOCATION (owner 2026-10-01: "add in desk listing share location, in the share link menu").
// A Google Maps link to the pin. On a phone it opens the share sheet (WhatsApp, Telegram…); elsewhere it copies.
// ⚠️ Offered ONLY when the viewer represents the flat AND a pin arrived — the SAME gate NearbyHover uses. The server
// degrades the pin for everyone else, and sending a degraded point to a client as "the location" would be a wrong
// address with our name on it. No coordinates → no row: never a menu item with nothing behind it.
export const shareableLocation = (lat, lng, isRep) =>
  isRep && lat != null && lng != null && Number.isFinite(+lat) && Number.isFinite(+lng) ? { lat: +lat, lng: +lng } : null
const mapsLink = ({ lat, lng }) => `https://www.google.com/maps?q=${lat.toFixed(6)},${lng.toFixed(6)}`

function LinksMenu({ listingId, onShareClient, close, location = null }) {
  const [copied, setCopied] = useState(false)
  const [locCopied, setLocCopied] = useState(false)
  async function shareLocation() {
    const url = mapsLink(location)
    try {
      // The share sheet where the device has one (phones); a cancelled sheet is not an error worth showing.
      if (navigator.share && /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent)) {
        await navigator.share({ url }).catch(() => {})
        close?.()
        return
      }
      await navigator.clipboard.writeText(url)
      setLocCopied(true)
      setTimeout(() => { setLocCopied(false); close?.() }, 900)
    } catch {
      close?.()   // clipboard blocked — no-op, same as the agent link
    }
  }
  const row = 'w-full text-left flex items-center gap-2 px-1 py-1.5 text-xs text-muted-foreground hover:text-foreground border-b border-border/40 last:border-0'
  async function copyAgentLink() {
    try {
      await navigator.clipboard.writeText(listingDeepLink(listingId))
      setCopied(true)
      setTimeout(() => { setCopied(false); close?.() }, 900)
    } catch {
      close?.()   // clipboard blocked (insecure context / denied) — no-op, same as CopyListingLink
    }
  }
  return (<>
    {onShareClient && (
      <button type="button" className={row} data-link-client
        onClick={() => { close?.(); onShareClient() }}>
        <Icon name="link" /> {t('share_kind_client')}
      </button>
    )}
    {listingId && (
      <button type="button" className={row} data-link-agent onClick={copyAgentLink}>
        <Icon name={copied ? 'check' : 'link'} /> {copied ? t('desk_share_copied') : t('share_kind_agent')}
      </button>
    )}
    {location && (
      <button type="button" className={row} data-share-location onClick={shareLocation}>
        <Icon name={locCopied ? 'check' : 'map-pin'} /> {locCopied ? t('desk_share_location_copied') : t('share_kind_location')}
      </button>
    )}
  </>)
}

export function ShareChip({ shares, onShare, listingId = null, solid = null, dashed = null, location = null }) {
  if (shares == null) return null
  const rows = shares || []
  // Fixed-width affordances never shrink; min-w-0 + truncate on the solid form —
  // same rationale as CardFooter's own copies, which pass theirs in so the rail
  // keeps sharing one definition of the class strings.
  const solidCls = solid || pill('neutral', { door: true, shrink: true, extra: 'bg-background overflow-hidden' })
  const dashedCls = dashed || pill('add', { door: true })
  return rows.length ? (
    <FacetChip cls={solidCls} editOnClick
      tooltip={rows.map(shareRowText).join('\n')}
      icon={<><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${shareSeenColor(rows)}`} /><Icon name="link" /></>}
      detailTitle={t('desk_share_chip_title')}
      rows={[
        ...rows.map(s => ({ key: s.id, label: <ShareRow s={s} /> })),
        // ⚠️ BOTH KINDS STAY REACHABLE once something has been sent. Before this the
        // populated face offered only "manage", so an agent who had already sent a client
        // link could not copy the AGENT link without emptying the list first.
        { key: '__links', label: <LinksMenu listingId={listingId} onShareClient={onShare} location={location} /> },
      ]}>
      {/* ⚠️ GLYPH + COUNT ONLY (owner 2026-08-03: "this must stay only emoji and x").
          The other footer chips put a number on the face because they have exactly ONE
          fact to state; this one is a LIST, and printing the newest link's price made
          the face read as "this property is 123,000" — a frozen quote to one client,
          standing where the card's own price would be. The prices belong in the rows,
          where each one is attached to the client and the date it was quoted. The
          count is always shown, even at 1: it is the difference between this and the
          dashed ＋ at a glance. */}
      <span className="opacity-70 shrink-0">×{rows.length}</span>
    </FacetChip>
  ) : (onShare || listingId || location) ? (
    <LinksDoor listingId={listingId} onShareClient={onShare} cls={dashedCls} location={location} />
  ) : null
}

// The dashed 🔗＋ used to fire the client-link modal directly. It is a MENU now, because the
// pill carries two named kinds (owner 2026-09-08) — and a door that opens one of two things
// without saying which is exactly what the two-kinds rule exists to prevent.
function LinksDoor({ listingId, onShareClient, cls, location = null }) {
  const [pos, setPos] = useState(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)
  const close = () => setPos(null)
  useCloseOnPageMove(!!pos, close, panelRef)
  useEffect(() => {
    if (!pos) return
    const onDocClick = e => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      close()
    }
    document.addEventListener('click', onDocClick, true)
    return () => document.removeEventListener('click', onDocClick, true)
  }, [pos])
  return (<>
    <button ref={btnRef} type="button" title={t('desk_share_chip_add')} className={cls}
      onClick={e => { e.stopPropagation(); pos ? close() : setPos(popoverPos(btnRef.current, 240)) }}>
      <Icon name="link" />＋
    </button>
    {pos && createPortal(
      <div ref={panelRef} style={{ ...pos, width: 240 }} onClick={e => e.stopPropagation()}
        className="desk-popover fixed z-[200] bg-card border border-border rounded-lg shadow-xl py-1.5 px-3 text-sm">
        <LinksMenu listingId={listingId} onShareClient={onShareClient} close={close} location={location} />
      </div>,
      document.body
    )}
  </>)
}

// §3 — date OR status, never both: the chip is already tinted by status, so printing the
// word beside the colour spends the width twice. An upcoming meeting is a WHEN; a finished
// one is a WHAT HAPPENED; an active one with no time set has only its status to show.
function meetingFaceLabel(v, at) {
  const upcoming = at != null && at >= Date.now()
  if (MEETING_ACTIVE.includes(v.status) && upcoming) return viewingSchedText(at)
  return t(MEETING_STATUS_KEYS[v.status]) || v.status
}

// ─── THE card footer (CARD_FOOTER_SPEC.md §1) ────────────────────────────────────────────
// Three lines, and each line shows EITHER its recorded facts OR its ＋, never both:
//   line 1  cooperation · meeting  — one token each (§2/§3), ×N when there are more
//   line 2  restrictions           — every chip at FULL label, in a horizontal scroller
//   line 3  rail                   — 📞 contact · ↗ share
// The stack's ×N arrives as `endSlot` and rides the RAIL row (2026-08-03). It used to be
// `absolute bottom-2 right-2` on the group wrapper with this note claiming it "already lands
// at the rail's right end" — it did not: the overlay forced the card to reserve a pb-8 strip
// BELOW the footer, so the chip sat on its own empty band. The wrapper still owns the chip's
// content and its expand handler; only the placement is ours.
//
// ⚠️ This REPLACES the "constant one-line row, never wrapping into a second row" contract
// of 2026-07-24 (owner ruling 2026-07-25). Do NOT restore it. Why it went: that row
// collapsed chips to icon+count once width ran out, which hid the very facts an agent opens
// a card to read — "✗ 3" says nothing, and it still cost the width. A LINE budget keeps the
// height constant instead, and the scroller lets an unbounded number of restriction chips
// live inside a bounded height. Height is now constant, so there is no `+N` and no cap.
//
// PRESENCE RULE (§1): filled → the chip opens the modal; empty → the ＋ opens the SAME
// modal. Two doors, one room — each of these modals already handles add and edit. A dashed
// affordance exists to represent ABSENCE, so it must disappear the moment the fact exists.
// `wide` (owner ruling 2026-07-25): the LINE BUDGET above is a response to ~355px of card,
// and it should not outlive that constraint. In the desktop single-column list view the card
// is `max-w-4xl` — roughly 900px — where three stacked rows leave most of the row empty and
// push the next card down for nothing. Given the room, the same three groups sit on ONE line
// in their ruled order. This is a WIDTH fact, so it is passed by the surface that owns the
// grid (HistoryItem, from gallerySize), never guessed here — and it is additionally gated on
// `md:` so a narrow desktop window still stacks. Mobile can no longer reach the wide form at
// all: the entries view derives away from the left-thumb card under 768px.
// `endSlot` (2026-08-03): the stack-count / thread-toggle chip, rendered at the END of the
// RAIL row. It used to be `absolute bottom-2 right-2` over the card, which forced the card to
// reserve a pb-8 strip under the footer — a whole empty band for one chip (owner: "it takes
// unnecessary space"). The rail comment below already stated the intended design — "📞 ↗ and
// the ×N stack count sit on the last line on every card" — so this makes it structurally true
// instead of approximating it with an overlay + a reservation. Being a real flex child, it can
// no longer land ON the ＋ affordances, which is what the reservation was defending against.
// `startSlot` (owner 2026-08-04: "this extra space is useless — we need to eliminate that"):
// feed-supplied count chips (leads' claim count, "in conversation") used to arrive as their
// OWN hairline band directly above this footer — a whole row of vertical space for two small
// counters, on every lead card. They ride the footer's row now. Same rationale as endSlot,
// which absorbed the stack-count chip for exactly this reason in the same week.
function CardFooter({ cooperations, offer = null, meetings, restrictions, payTerms = null,
  payRent = null, payRentCurrency = null, shares = null, onOpen, onAdd, onAddMeeting, onCreateContract, onShare, onRepost, onOpenTerms, onOpenMeeting, contactListing, contactKnown = null, authorMark = null, privileged, hairline, wide = false, endSlot = null, startSlot = null, onShareOthers = null }) {
  const coops = cooperations || []
  const meets = meetings || []
  const structuredRestrictions = useStructuredRestrictionFace(contactListing?.listing_id)
  // The face count means OTHER DISTINCT conditions. Legacy and structured sources can
  // briefly contain the same owner statement during migration, so collapse duplicates
  // before choosing the first label or calculating +N.
  const restrictionKeys = new Set()
  const rItems = [...(restrictions?.items || []), ...structuredRestrictions].filter(item => {
    const key = String(item?.label || '').trim().toLocaleLowerCase()
    if (!key || restrictionKeys.has(key)) return false
    restrictionKeys.add(key)
    return true
  })
  // EVERY link this agent minted for the property, newest first (server order) — revoked and
  // expired ones included, per the owner's "all the links agent created must be there". A
  // revoked link is not an un-event: the client was quoted that number and may still be
  // arguing from it, so it stays in the list, labelled dead, rather than being tidied away.
  const shareRows = shares || []
  // B159: the card's own ad decides which cooperation is "best" when several intents sit on
  // one property — a sale card must not face a rent deal just because 100 > 3.
  const best = coops.length ? bestCooperation(coops, coopIntentOf(contactListing?.intent)) : null
  const mActive = meets.filter(v => MEETING_ACTIVE.includes(v.status))
  // ⚠️ pickClosestMeeting returns orderMeetings()' `{ v, at }` PAIR, not the viewing row —
  // destructure it. Reading `.status`/`.events` off the pair yields undefined all the way
  // through to a blank face token (caught in the harness, 2026-07-25), silently: every
  // lookup degrades to a default instead of throwing.
  const mLead = meets.length ? pickClosestMeeting(mActive.length ? mActive : meets) : null
  const mV = mLead?.v || null
  const mAt = mLead?.at ?? null
  // allowed/preferred joined 2026-07-29 (✓-lens presentation): while an allow-mode
  // restriction filter is active, IntakeTab's restrDisplayByListing prepends the matched
  // positive stance so the card face shows WHY it qualified.
  const R_ICON = { not_allowed: '✗', only: '❗', allowed: '✓', preferred: '✓' }
  const POS_STANCE = s => s === 'allowed' || s === 'preferred'

  const dashed = pill('add', { door: true })
  // min-w-0 + truncate: the FACE tokens are short by construction (§2/§3), but a long
  // currency or a status word in a wide locale must ellipsise rather than push its sibling
  // off the line. Fixed-width affordances never shrink — that was the 2026-07-24 lesson
  // (making everything shrink ellipsised the ＋ glyph itself into a plain-looking button).
  const solid = pill('neutral', { door: true, shrink: true, extra: 'bg-background overflow-hidden' })

  // ⚠️ ORDER (owner ruling 2026-07-25): RAIL first — "number and share" — then the DEAL pair
  // (cooperation, meeting), then restrictions. Named for their content, not numbered: the
  // numbering in CARD_FOOTER_SPEC.md §1 described the OLD sequence and would now mislead.
  // Reading order follows what an agent does with a card: reach the owner, then record what
  // was agreed, then note the conditions attached to it.
  // endSlot rides the rail, so it also KEEPS the rail: a stacked card whose only footer
  // content is the count chip still needs the row to exist, or the caller's chip vanishes
  // (the caller has already stopped drawing its own overlay by then).
  const rowRail = (contactListing?.property_id || onShare || endSlot || startSlot)
  const rowDeal = (coops.length || onAdd || meets.length || onAddMeeting || onCreateContract)
  const rowRestrictions = (rItems.length || onOpenTerms)
  if (!rowRail && !rowDeal && !rowRestrictions) return null

  // ⚠️ TWO ORDERS, and the difference is deliberate (owner correction 2026-07-25 — the first
  // pass wrongly moved the rail to the top everywhere):
  //   STACKED (mobile, and the desktop 3-up grid) — deal, restrictions, then the RAIL AT THE
  //     BOTTOM, unchanged. The rail is the card's floor: 📞 ↗ and the ×N stack count sit on
  //     the last line on every card whatever else is present, so the thumb always lands in
  //     the same place. Moving it to the top cost that constancy for nothing — a stacked
  //     footer has no left-to-right reading order to fix.
  //   ONE LINE (desktop list card) — rail FIRST, then deal, then restrictions.
  // The DOM stays in stacked order and the wide form reorders with flex `order`, because
  // `order` is inert outside a flex container: the stacked form is `space-y-*` on a block
  // box, so the same classes cannot leak into it. Applied only when `wide` anyway.
  // ⚠️ Only `restrictions` is left. The rail and deal keys went when the one-line form merged
  // its pills into a single row (owner 2026-08-09): that row carries `md:order-2` directly and
  // the start counter `md:order-1`, so there is nothing left for `ord` to place except the
  // restriction scroller, which is still its own element and still comes last.
  const ord = wide ? { restrictions: 'md:order-3' } : { restrictions: '' }

  // ── THE PILLS, HOISTED (B637 follow-up, owner 2026-08-09) ──────────────────────────────
  // *"No pills get grouped inside three dot."* True, and it was the cost of the previous fix:
  // making each GROUP content-sized stopped the gap but also meant no group could ever be too
  // narrow, so nothing folded. The groups were the wrong unit. On a one-line card the owner
  // asked for exactly one row — *"align pills all in line then fold one by one"* — which also
  // puts the `⋮` at the END of the row instead of mid-row, where a per-group fold had put it.
  //
  // So the pills are declared ONCE here and arranged differently by layout below: one merged
  // row when the footer is a line, the three ruled groups when it is stacked.
  // ⚠️ The stacked form keeps its three lines and its rail-at-the-bottom order (owner ruling
  // 2026-07-25) — that ruling is about a stacked card and is untouched.
  const unorderedDealItems = pills(
        coops.length ? { key: 'coop', title: t('desk_coop_modal_title'), node: (
          <FacetChip cls={solid} tooltip={coopHoverLines(coops)}
            icon={<><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${coopStatusColor(best.status)}`} /><Icon name="handshake" /></>}
            rows={coops.map(c => ({
              key: c.id,
              label: [coopChipLabel(c), t(COOP_STATUS_KEYS[c.status]) || c.status].filter(Boolean).join(' · '),
              onOpen: () => onOpen(c),
            }))}
            onEdit={() => onOpen(best)} editLabel={t('edit')}>
            <span className="truncate">{coopFaceLabel(best)}</span>
            <AiMark mark={coopStanceMark(best, offer)} />
            {coops.length > 1 ? <span className="opacity-70 shrink-0">×{coops.length}</span> : null}
          </FacetChip>
        ) } : (offerShows(offer) && onAdd) ? { key: 'coop-offer', title: t('desk_coop_offer_tooltip'), node: (
          <CoopOfferChip offer={offer} onAdd={onAdd} />
        ) } : onAdd ? { key: 'coop-add', title: t('desk_coop_open_tooltip'), node: (
          <button type="button" title={t('desk_coop_open_tooltip')} className={dashed}
            onClick={e => { e.stopPropagation(); onAdd() }}><Icon name="handshake" />＋</button>
        ) } : null,

        onCreateContract ? { key: 'contract-add', title: 'ხელშეკრულება · Contract', node: (
          <button type="button" title="ხელშეკრულება · Contract" className={dashed}
            onClick={e => { e.stopPropagation(); onCreateContract() }}><Icon name="file-text" />＋</button>
        ) } : null,

        meets.length ? { key: 'meet', title: t('desk_meeting_list_title'), node: (
          <FacetChip cls={pill('none', { door: true, shrink: true, extra: `overflow-hidden ${mActive.length
            ? (VIEWING_STATUS_CARD_STYLE[mV.status] || 'border-border bg-background text-muted-foreground')
            : 'border-border bg-background text-muted-foreground'}` })}
            tooltip={meetingHoverLines(meets, privileged)}
            icon={<Icon name="calendar" />}
            detailTitle={t('desk_meeting_list_title')}
            /* ⚠️ THE ＋ DOOR LIVES HERE ONCE A MEETING EXISTS (owner-reported 2026-08-15:
               "made a meeting and cancelled it, now I want a new one — how?"). The dashed
               calendar＋ below only renders while `meets.length === 0`, so the moment ANY
               meeting exists — including a cancelled one — the chip replaced it and the
               card face offered no way to book a second. A meeting is not a slot a
               property has one of; it is an event that recurs.
               Two consequences, both wanted: the row is the add door on every card that
               has meetings, and its presence makes `rows.length > 1` true for a single
               meeting, so a mouse click opens THE LIST (history + add) instead of jumping
               straight into the one record — which is the other half of the same report. */
            rows={[
              ...orderMeetings(meets).map(({ v, at }) => ({
                key: v.id, label: meetingChipLabel(v, at, privileged), onOpen: () => onOpenMeeting?.(v),
              })),
              ...(onAddMeeting ? [{
                key: '__add', accent: true,
                label: `＋ ${t('desk_meeting_add_tooltip')}`,
                onOpen: () => onAddMeeting(),
              }] : []),
            ]}
            onEdit={() => onOpenMeeting?.(mV)} editLabel={t('edit')}>
            {mAt && MEETING_ACTIVE.includes(mV.status)
              ? <MeetingCountdown at={mAt} status={mV.status} showIcon={false} className="truncate" />
              : <span className="truncate">{meetingFaceLabel(mV, mAt)}</span>}
            {meets.length > 1 ? <span className="opacity-70 shrink-0">×{meets.length}</span> : null}
          </FacetChip>
        ) } : onAddMeeting ? { key: 'meet-add', title: t('desk_meeting_add_tooltip'), node: (
          <button type="button" title={t('desk_meeting_add_tooltip')} className={dashed}
            onClick={e => { e.stopPropagation(); onAddMeeting() }}><Icon name="calendar" />＋</button>
        ) } : null,
  )
  // Meetings lead the deal sequence: what is scheduled comes before the commercial
  // arrangement, then the owner's conditions follow immediately after cooperation.
  const dealRank = { 'contract-add': 0, meet: 1, 'meet-add': 1, coop: 2, 'coop-offer': 2, 'coop-add': 2 }
  const dealItems = [...unorderedDealItems].sort((a, b) => (dealRank[a.key] ?? 9) - (dealRank[b.key] ?? 9))
  const railItems = pills(
          contactListing?.property_id && { key: 'contact', title: t('desk_coop_chip_default'), node: (
          /* `contactKnown` (owner ruling 2026-07-25): THE one contact affordance on the
             card — solid when a number is on file, dashed when none is. The Leads feed
             used to render a SECOND ContactPill in its strip to carry that dashed state,
             which put two phone buttons on one card doing the same thing. The state
             belongs on the control, so the feed passes the fact down instead.
             null (property/meeting/client cards) keeps the plain solid pill: those
             surfaces do not know whether a number exists, and guessing `false` would
             make every card advertise a missing contact it never checked. */
          <ContactPill listing={contactListing} chip iconOnly known={contactKnown} agentMark={authorMark}
            label={contactKnown === false ? t('desk_lead_no_phone') : t('desk_coop_chip_default')} />
        )},
          /* ↗ not 🔗 (owner 2026-07-25): the FACE names the verb (share); the link glyphs
             belong to the two KINDS inside the menu. Solid, not dashed — dashed is reserved
             for absence/add (§1, and designer note 3 uses it for unconfirmed restrictions),
             and share is a verb that is always present. THE one share affordance: the
             lineage modal's footer renders this same component. */
          // ↗ is the SHARE pill now: acts on the thing itself. The link kinds moved to 🔗
          // below (owner 2026-09-08), so this renders only when there is a repost to offer —
          // `ShareMenu` returns null otherwise rather than opening an empty menu.
          ((onRepost || onShareOthers) && contactListing?.listing_id) && { key: 'share', title: t('share_menu_tooltip'), node: (
            <ShareMenu
              onShareOthers={onShareOthers && contactListing?.property_id
                ? () => onShareOthers(contactListing) : null}
              onRepost={onRepost && contactListing?.listing_id
                /* The WHOLE row, not just the id (owner-reported 2026-07-31): the repost door
                   opens the combined DealEditor, whose coop/terms tabs seed from the card's
                   listing (author, intent, price). With only an id those tabs opened empty —
                   the same bug the 🏠＋/🤝＋ doors already solved by passing item.listing. */
                ? () => onRepost(contactListing) : null} />
          ) },
          /* 🔗 THE LINKS PILL — both kinds live here now, beside the history of what has
             already been sent, because that history is the answer the links raise:
             "have I already sent this, and to what number?" */
          { key: 'links', title: t('desk_share_chip_title'), node: (
            <ShareChip shares={shareRows} onShare={onShare} listingId={contactListing?.listing_id}
              solid={solid} dashed={dashed}
              location={shareableLocation(contactListing?.lat, contactListing?.lng, contactListing?.is_representative)} />
          ) },
  )
  // 🏠＋ joins the merged row only when there are no restriction CHIPS; with chips the group is
  // a horizontal scroller that must stay its own element (its four traps are documented below).
  const termsItem = (!rItems.length && onOpenTerms) ? { key: 'terms', title: t('desk_terms_facet_title'), node: (
    <button type="button" title={t('desk_terms_facet_title')} className={dashed}
      onClick={e => { e.stopPropagation(); onOpenTerms() }}><Icon name="house" />＋</button>
  ) } : null
  // ── WHAT THE TENANT PAYS BEFORE THE KEYS (2026-09-12) ─────────────────────────────────
  // Until now this reached ONE screen — the property's terms tab — while the card, which is
  // where an agent actually scans, said nothing. 753 listings carry a prepayment or deposit
  // read out of their own advert, and „დეპოზიტი: ნახევარი თვე" on a 600 USD flat was a fact
  // the corpus held and no surface showed.
  //
  // 🔑 THE LABEL COMES FROM lib/tenancyTerms, NOT FROM HERE. moveInPayment already decides
  // what is owed and what is unknown, and moveInLabel turns that into a headline; a second
  // summariser in this component would be a second opinion on the same numbers and would
  // drift the first time one of them learned a new field.
  //
  // ⚠️ A tilde means the list is UNFINISHED, not that the figure is approximate — it appears
  // whenever a part is unpriced, the currencies are mixed, or a prepay flag was never
  // answered. A bare number an agent quotes to a client must never imply we asked everything.
  const payLabel = (() => {
    if (!payTerms) return null
    const tri = v => (v === true ? true : v === false ? false : null)
    const m = moveInPayment({
      prepayFirst: tri(payTerms.prepay_first_month), prepayLast: tri(payTerms.prepay_last_month),
      depositAmount: payTerms.deposit_amount, depositCurrency: payTerms.deposit_currency,
      depositMonths: payTerms.deposit_months,
      rent: payRent, rentCurrency: payRentCurrency,
    })
    const lab = moveInLabel(m, t)
    if (lab) return lab
    // Nothing priced, but the owner DID say a deposit is demanded — the 278-advert case
    // (1083). The requirement alone is worth a pill; an agent budgets differently for
    // "there is a deposit, amount unknown" than for silence.
    return payTerms.deposit_required === true
      ? { head: t('desk_terms_deposit_required_short'), rows: [], complete: false }
      : null
  })()
  // 1082: a term Idmon read out of the advert says so, exactly as a feature does. The row is
  // property_feature-shaped on purpose (source_kind / source_agent / source_version), so
  // aiMarkFor needs no new shape — and it silences itself when a human recorded the row.
  const payMark = payLabel ? aiMarkFor({
    source_kind: payTerms.source_kind, source_agent: payTerms.source_agent,
    source_version: payTerms.source_version, last_seen_at: payTerms.recorded_at, instances: null,
  }) : null
  const payItem = payLabel ? { key: 'movein', title: t('desk_movein_heading'), node: (
    <FacetChip cls={pill('neutral', { door: true, extra: 'bg-background text-muted-foreground overflow-hidden' })}
      style={{ maxWidth: '9rem' }}
      editOnClick tooltip={[t('desk_movein_heading'), ...payLabel.rows.map(r => r.label)].join('\n')}
      icon={<Icon name="dollar-sign" />}
      detailTitle={t('desk_movein_heading')} rows={payLabel.rows}
      onEdit={onOpenTerms} editLabel={t('edit')}>
      <span className="min-w-0 truncate">{payLabel.head}</span>
      {payMark && <AiMark mark={payMark} className="shrink-0" />}
    </FacetChip>
  ) } : null
  const restrictionsHover = rItems.map(x => `${R_ICON[x.stance] || ''} ${x.label}${x.ai ? ' ?' : ''}`).join('\n')
  const restrictionItems = rItems.length ? pills(
    { key: 'terms-first', title: t('desk_terms_facet_title'), node: (() => {
      const r = rItems[0]
      return <FacetChip cls={pill(r.ai ? 'add' : 'neutral', { door: true, extra: 'bg-background text-muted-foreground overflow-hidden' })}
        style={{ maxWidth: '7.5rem' }}
        editOnClick tooltip={restrictionsHover} icon={<Icon name={POS_STANCE(r.stance) ? 'check' : 'ban'} />}
        detailTitle={t('desk_terms_facet_title')}
        rows={rItems.map(x => ({ key: x.label, label: `${R_ICON[x.stance] || ''} ${x.label}${x.ai ? ' ?' : ''}` }))}
        onEdit={onOpenTerms} editLabel={t('edit')}>
        <span className="min-w-0 truncate">{POS_STANCE(r.stance) ? '' : (R_ICON[r.stance] || '')} {r.label}{r.ai ? '?' : ''}</span>
        {rItems.length > 1 && <span className="shrink-0 opacity-70">+{rItems.length - 1}</span>}
      </FacetChip>
    })() },
  ) : (termsItem ? [termsItem] : [])
  // One measured row on wide cards: rail → meeting → cooperation → restrictions.
  // After the restrictions: a rule about WHO may live here outranks the bill, and the
  // bill outranks nothing else on this row — it folds first when the card narrows.
  const oneRowItems = [...railItems, ...dealItems, ...restrictionItems, ...(payItem ? [payItem] : [])]
  return (
    // `hairline` (A→C 2.2): the separator is drawn HERE, not by the caller — this component
    // is the only place that knows the footer has content. A caller drawing it renders a
    // hairline over nothing on every card whose footer is empty, which reads as broken.
    // `desk-card-footer` is a STYLING HOOK, not decoration: index.css uses it to override
    // the global 44px mobile touch-target height for these pills only (owner ruling
    // 2026-07-25 — lower and wider beats tall and truncated). Don't rename without it.
    <div className={`desk-card-footer pointer-events-auto ${wide ? 'space-y-1 md:space-y-0 md:flex md:items-center md:gap-3' : 'space-y-1'} ${hairline ? 'border-t border-border/50 mt-1.5 pt-1.5' : 'mt-2'}`}>
      {/* ── ONE LINE: one row, one ⋮, folding from the END ────────────────────────────────
          Owner 2026-08-09: *"align pills all in line then fold one by one"*, after the
          per-group version put a ⋮ in the MIDDLE of the row (the rail is visually first at
          `md`, so its overflow button landed before the deal pills) and, once the gap fix made
          each group content-sized, stopped folding at all.
          Merging is what makes the ⋮ mean "the rest of this row": there is only one row.
          ⚠️ No `grow` and no `shrink-0` anywhere on this child. It must be content-sized when
          it fits (or it re-creates the gap) and shrinkable when it does not (or nothing ever
          folds) — see the three failed attempts recorded in PillOverflow.jsx. */}
      {wide ? (
        // ⚠️ `md:contents` — the wrapper exists ONLY below `md`, where the footer is a block and
        // these would otherwise become three stacked lines (start counter, pills, count) instead
        // of the single row the owner asked for. At `md` the box dissolves and its children
        // become direct flex items of the footer, so the `order` classes still rule the line.
        <div className="flex items-center gap-1.5 min-w-0 md:contents">
          {startSlot && <span className="md:order-1 shrink-0">{startSlot}</span>}
          {oneRowItems.length > 0 && <PillOverflow className="md:order-2" items={oneRowItems} />}
          {/* Below `md` the ×N count is pinned right by this copy; at `md` the footer-level
              sibling near the end of this file takes over (it can push against the footer's own
              slack, which a content-sized item cannot). Two CSS-exclusive copies, one handler. */}
          {endSlot && <span className="ml-auto shrink-0 md:hidden">{endSlot}</span>}
        </div>
      ) : (
        dealItems.length > 0 && <PillOverflow items={dealItems} />
      )}
      {rowRestrictions && !wide && (
        rItems.length ? (
          // ⚠️ Four traps live in this scroller (§5):
          // 1. NO drag-to-scroll — the card face is click-to-open, so dragging would need a
          //    drag/click discriminator. Touch swipe fires no click; a mouse gets the wheel.
          // 2. overscroll-contain — without it a swipe past the last chip chains outward,
          //    and on iOS Safari that is the edge-swipe BACK gesture: the agent leaves the
          //    Desk by trying to read a restriction.
          // 3. scrollbar hidden + right-edge fade — a bare cut-off row reads as broken
          //    rendering, and with drag off the table the fade is the only "there's more"
          //    signal a mouse user gets.
          // 4. this must never inherit an ellipsis/overflow rule from the row above, which
          //    would truncate the SCROLLER itself into a single "…".
          // `min-w-0 flex-1` on ONE line: this is the group that absorbs the leftover width
          // and the only one that can survive being squeezed (it scrolls). Without flex-1 it
          // would size to its content and push the row wider than the card.
          <div className={`relative min-w-0 md:flex-1 ${ord.restrictions}`}>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar overscroll-x-contain pr-4"
              style={{ scrollbarWidth: 'none', touchAction: 'pan-x' }}>
              {rItems.slice(0, 1).map(r => (
                <FacetChip key={r.label}
                  cls={pill(r.ai ? 'add' : 'neutral', { door: true, extra: 'bg-background text-muted-foreground overflow-hidden' })}
                  style={{ maxWidth: '7.5rem' }}
                  editOnClick
                  tooltip={rItems.map(x => `${R_ICON[x.stance] || ''} ${x.label}${x.ai ? ' ?' : ''}`).join('\n')}
                  icon={<Icon name={POS_STANCE(r.stance) ? 'check' : 'ban'} />}
                  detailTitle={t('desk_terms_facet_title')}
                  rows={rItems.map(x => ({ key: x.label, label: `${R_ICON[x.stance] || ''} ${x.label}${x.ai ? ' ?' : ''}` }))}
                  onEdit={onOpenTerms} editLabel={t('edit')}>
                  {/* positive stances: the check icon IS the glyph — no doubled ✓ */}
                  <span className="min-w-0 truncate">{POS_STANCE(r.stance) ? '' : (R_ICON[r.stance] || '')} {r.label}{r.ai ? '?' : ''}</span>
                  {rItems.length > 1 && <span className="shrink-0 opacity-70">+{rItems.length - 1}</span>}
                </FacetChip>
              ))}
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 w-4 bg-gradient-to-l from-card to-transparent" />
          </div>
        ) : (!wide && termsItem) ? (
          // Stacked only: on a one-line card this same 🏠＋ is already IN the merged row above
          // (it is `termsItem`), and rendering it here too would put two of them on the card.
          <div className="flex items-center gap-1.5 shrink-0">{termsItem.node}</div>
        ) : null
      )}

      {/* RAIL — stacked form only. It is LAST in the DOM so a stacked card keeps it on the
          bottom line: the rail is that card's floor, so the thumb always lands in the same
          place (owner ruling 2026-07-25, unchanged).
          ⚠️ On a ONE-LINE card these same pills are already in the merged row above, so this
          must not render there or the card shows 📞/↗/🔗 twice. */}
      {!wide && rowRail && (
        <div className="flex items-center gap-1.5 min-w-0">
          {/* Feed-supplied counters lead the rail — they are FACTS about the card, and the
              affordances after them are what you do about it. */}
          {startSlot}
          {railItems.length > 0 && <PillOverflow items={railItems} />}
          {/* ×N pinned to the card's bottom-right corner (owner 2026-08-03: "put x4 to the
              most right of the card"). Outside the overflow set on purpose — an `ml-auto` item
              inside a measured prefix eats all the slack and makes every other pill look as
              though it did not fit. */}
          {endSlot && <span className="ml-auto shrink-0">{endSlot}</span>}
        </div>
      )}
      {/* ONE-LINE form: the chip is a child of the FOOTER, last in the ruled order and pushed
          to the far right. It has to live here rather than inside the rail — a flex item can
          only push against slack its own container owns, and the rail is content-sized. When
          restrictions are present they hold `md:flex-1` and absorb the slack instead, so the
          chip still ends at the card's right edge, with the scroller filling the gap. */}
      {endSlot && wide && (
        <span className="hidden md:flex md:items-center md:order-4 md:ml-auto shrink-0">{endSlot}</span>
      )}
    </div>
  )
}

// ─── MODAL surfaces (CARD_FOOTER_SPEC.md §8, owner ruling 2026-07-25) ─────────────────────
// The card face took the three-line CardFooter above; the modal surfaces that render these
// chips (LineageModal shop-window, PersonModal, ManualPropertyModal) keep this WRAPPING row.
// Brought in line on the thing that actually mattered — the §4 interaction contract — and
// deliberately NOT on density: a modal has room, so its chips keep the FULL label
// (coopChipLabel) instead of the card's one-token face, and list every cooperation rather
// than collapsing to the best one. Same rule, different space.
//
// Two questions that looked like blockers, answered without a product decision:
//  · PersonModal is anchored on a PARTY, which has no restrictions and no meetings. That is
//    "not applicable to this anchor", NOT "nothing recorded yet" — and only the latter earns
//    a ＋. So those lines simply don't render here; a ＋ opening an editor for a concept the
//    anchor doesn't have would be worse than the inconsistency it removed.
//  · The shop window is read-only for a non-representative viewer (ruling 2026-07-14). It
//    gets the same RENDERING, not a new 🤝＋ — layout parity must not quietly grant a write
//    permission. Whether a non-rep may propose a cooperation is a product call, not this one.
//
// `oneLine`/`compact`/`tight` REMOVED 2026-07-25: the card face was `oneLine`'s only caller
// and moved to `cardFooter`, `compact` had no caller at all and survived only through
// `tight = compact || oneLine`. With both gone the collapse-to-best-deal branch is dead too,
// which is correct — collapsing is a card-face behaviour and this path is modals only.
// `hairline` (A→C 2.2): the separator is drawn HERE, not by the caller. This component is the
// only place that knows the row will be empty — a caller that draws the border itself renders
// a hairline over nothing whenever the row has no content, which reads as a broken card.
export function CooperationChips({ cooperations, offer = null, shares = null, onOpen, onAdd, onAddMeeting, onCreateContract, onShare, onRepost, onOpenTerms, contactListing, contactKnown = null, authorMark = null, privileged = false, cardFooter = false, hairline = false, wide = false, meetings = null, onOpenMeeting = null, restrictions = null, endSlot = null, startSlot = null, onShareOthers = null }) {
  // endSlot counts as content: it is the caller's ONLY rendering of the chip now (HistoryItem
  // stops drawing the overlay whenever it hands one down), so an empty-footer bail here would
  // silently delete a stack's count.
  if (!cooperations?.length && !meetings?.length && !shares?.length && !onAdd && !onAddMeeting && !onCreateContract && !onShare && !onOpenTerms && !contactListing && !endSlot && !startSlot) return null
  if (cardFooter) return (
    <CardFooter cooperations={cooperations} offer={offer} meetings={meetings} restrictions={restrictions}
      onOpen={onOpen} onAdd={onAdd} onAddMeeting={onAddMeeting} onCreateContract={onCreateContract} onShare={onShare} onRepost={onRepost} onShareOthers={onShareOthers}
      onOpenTerms={onOpenTerms} onOpenMeeting={onOpenMeeting} contactListing={contactListing}
      contactKnown={contactKnown} authorMark={authorMark} privileged={privileged} hairline={hairline} wide={wide} endSlot={endSlot} startSlot={startSlot}
      shares={shares} />
  )
  const coops = cooperations || []
  const shown = coops
  return (
    <div className={`flex flex-wrap gap-1.5 pointer-events-auto ${hairline ? 'border-t border-border/50 mt-1.5 pt-1.5' : 'mt-2'}`}>
      {/* Contacts chip — view the property's contacts AND save a caller's number ("client
          called, agent answered, keep their contact") without opening the lineage modal; the
          popover carries ＋ Add contact inside. LEADS the row (2026-07-17, user-directed):
          it's the one chip present on every property card, so a fixed first position makes
          it findable without scanning. */}
      {contactListing?.property_id && <ContactPill listing={contactListing} chip label={t('desk_coop_chip_default')} />}
      {/* §8: FacetChip, not a bare title= button — the §4 contract (touch gets a detail
          view with an Edit door, never a straight jump into the editor) applies to modal
          chips exactly as to card chips; a modal is no less a touch surface. The label
          stays FULL (coopChipLabel) and every cooperation renders — the card's one-token
          face and best-deal collapse are density answers to a space problem modals
          don't have. rows carry each cooperation so touch can pick one from the detail. */}
      {shown.map(c => (
        <FacetChip key={c.id}
          cls={pill('neutral', { door: true, shrink: true, extra: 'bg-background' })}
          tooltip={tLog('desk_coop_chip_tooltip', { status: t(COOP_STATUS_KEYS[c.status]) || c.status })}
          icon={<><span className={`w-1.5 h-1.5 rounded-full shrink-0 ${coopStatusColor(c.status)}`} /><Icon name="handshake" /></>}
          rows={[{ key: c.id,
            label: [coopChipLabel(c), t(COOP_STATUS_KEYS[c.status]) || c.status].filter(Boolean).join(' · '),
            onOpen: () => onOpen(c) }]}
          onEdit={() => onOpen(c)} editLabel={t('edit')}>
          <span>{coopChipLabel(c)}</span>
          <AiMark mark={coopStanceMark(c, offer)} />
        </FacetChip>
      ))}
      {/* Closest meeting on the card face (2026-07-11; fill-state spec + designer notes
          2026-07-23) — "is someone already taking a client there, and when" is a
          property-face fact like the cooperation. The whole variant system lives in
          MeetingChip above. */}
      {meetings?.length > 0 && (
        <MeetingChip meetings={meetings} privileged={privileged} onOpenMeeting={onOpenMeeting} />
      )}
      {onAdd && !(cooperations?.length) && (offerShows(offer)
        ? <CoopOfferChip offer={offer} onAdd={onAdd} />
        : <button title={t('desk_coop_open_tooltip')}
            onClick={e => { e.stopPropagation(); onAdd() }}
            className={pill('add', { door: true })}>
            <Icon name="handshake" />＋
          </button>)}
      {/* Same chip row, same shape as 🤝＋ — schedule a viewing on this property without
          opening the lineage modal first. */}
      {onAddMeeting && (
        <button title={t('desk_meeting_add_tooltip')}
          onClick={e => { e.stopPropagation(); onAddMeeting() }}
          className={pill('add', { door: true })}>
          <Icon name="calendar" />＋
        </button>
      )}
      {/* Same chip row again — mint a client-facing link for this property
          (core/MEETINGS_MODEL.md §5). The workflow starts here: the client's
          representative sends the link, the client reviews and decides, and only
          then is a meeting worth booking. */}
      {onShare && (
        <button title={t('share_tooltip')}
          onClick={e => { e.stopPropagation(); onShare() }}
          className={pill('add', { door: true })}>
          <Icon name="link" />＋
        </button>
      )}
      {/* Rental restrictions / sale conditions (2026-07-17; fill-state spec 2026-07-23) —
          FILLED state: max 2 subject chips + a "+N" overflow (the §1 search-chip rule: 10
          recorded stances render as 2 + "+8", never a wrapped wall). ✗ = not_allowed,
          ❗ = only-this (STANCE_ICON — "only students" restricts everyone else too).
          Dashed + ? = scrape/AI-extracted, unconfirmed (dashed is RESERVED for exactly
          that meaning — designer note 3); solid = agent/owner-recorded. Every chip opens
          the same terms editor the bare 🏠＋ opens; hover lists everything. */}
      {onOpenTerms && restrictions?.items?.length ? (() => {
        // allowed/preferred joined 2026-07-29 (✓-lens presentation): while an allow-mode
        // restriction filter is active the overlay prepends the matched positive stance,
        // and a ✓ chip must not wear the ban icon.
        const R_ICON = { not_allowed: '✗', only: '❗', allowed: '✓', preferred: '✓' }
        const all = restrictions.items
        const shownR = all.slice(0, 2)
        const hover = all.map(r => `${R_ICON[r.stance] || ''} ${r.label}${r.ai ? ' ?' : ''}`).join('\n')
        const chipCls = ai => pill(ai ? 'add' : 'neutral', { door: true, extra: 'bg-background text-muted-foreground' })
        // The `oneLine` icon+count collapse that used to live here is GONE (2026-07-25):
        // it was the card face's answer to running out of width, and the card face now
        // scrolls its restrictions instead of counting them (CARD_FOOTER_SPEC.md §1/§5).
        // Modals were always the "2 chips + N" branch and keep it — they have the room.
        return (<>
          {shownR.map(r => (
            <button key={r.label} title={hover} className={chipCls(r.ai)}
              onClick={e => { e.stopPropagation(); onOpenTerms() }}>
              {/* positive stances: the check icon IS the glyph — no doubled ✓ */}
              <Icon name={r.stance === 'allowed' || r.stance === 'preferred' ? 'check' : 'ban'} /> {r.stance === 'allowed' || r.stance === 'preferred' ? '' : (R_ICON[r.stance] || '')} {r.label}{r.ai ? '?' : ''}
            </button>
          ))}
          {all.length > 2 && (
            <button title={hover} className={chipCls(false)}
              onClick={e => { e.stopPropagation(); onOpenTerms() }}>
              <Icon name="ban" /> +{all.length - 2}
            </button>
          )}
        </>)
      })() : onOpenTerms && (
        <button title={t('desk_terms_facet_title')}
          onClick={e => { e.stopPropagation(); onOpenTerms() }}
          className={pill('add', { door: true })}>
          <Icon name="house" />＋
        </button>
      )}
      {/* B237 — THE THIRD STATE. Before this, "the owner has no restrictions" and "nobody
          has asked" were the same pixel (the bare 🏠＋ above) and the same data. This chip
          is what makes silence mean only one thing.
          Rendered AFTER the subject chips on purpose: when both are present the card reads
          "✗ indians · ✓ no other restrictions", which is exactly the claim being made —
          none APART from what is recorded. `clearedHidden` is the earned-knowledge case:
          someone answered, this viewer hasn't recorded their own, so the answer is withheld
          — but it must still not read as unasked (hidden ≠ absent, VISIBILITY.md §1.4). */}
      {onOpenTerms && restrictions?.cleared && (
        <button
          title={restrictions.clearedHidden ? t('desk_terms_cleared_hidden_title') : t('desk_terms_cleared_title')}
          onClick={e => { e.stopPropagation(); onOpenTerms() }}
          className={pill(restrictions.clearedHidden ? 'add' : 'ok', { door: true })}>
          <Icon name={restrictions.clearedHidden ? 'lock' : 'check'} />
          {' '}{restrictions.clearedHidden ? t('desk_terms_cleared_hidden') : t('desk_terms_cleared')}
        </button>
      )}
    </div>
  )
}
